/** @jsxRuntime automatic */
/** @jsxImportSource react */
import React, { useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"
import { dashboardMarkdownHtml } from "@/datox/dashboard-markdown"
import { DashboardPdfPreview } from "./DashboardPdfPreview"
import { DashboardKpiContext, KpiDisplaySettings, KpiFormulaSettings, KpiWidget } from "./KpiWidget"
import { KpiSourcePanel } from "./KpiSourcePanel"
import { DashboardInspectorResize } from "./DashboardInspectorResize"
import { TableSettingsEditor } from "./TableSettingsEditor"
import { FreeStyleEditor } from "./FreeStyleTableEditor"
import { EditorNumberInput, EditorSection } from "./WidgetEditorFields"
import { chartItems, formattedCell, numericCell, tableSummary } from "./dashboard-widget-data"
import {
  compareDashboardCells,
  dashboardKpiPageHeight,
  dashboardWidgetHeight,
  waitForDashboardKpis,
} from "@/datox/dashboard-kpi"
import { Rnd } from "react-rnd"
import ReactECharts from "echarts-for-react"
import {
  alignmentGuides,
  boundedGroupMove,
  canvasAutoHeight,
  marqueeSelection,
  nearestFreePlacement,
  placementCollides,
  snapLayout,
  snapValue,
} from "./dashboardCanvas"
import {
  TbEdit,
  TbChartBar,
  TbChartDots,
  TbChartLine,
  TbChartPie,
  TbFileExport,
  TbPlus,
  TbFilter,
  TbFlame,
  TbGauge,
  TbLayoutGrid,
  TbLayoutList,
  TbTable,
  TbTextCaption,
  TbTrash,
  TbTypography,
  // TbArrowNarrowDown,
  // TbArrowNarrowUp,
  TbChevronLeft,
  TbChevronRight,
} from "react-icons/tb"
import { KpiComparisonDisplay, KpiComparisonSettings, useDashboardKpiComparisons } from "./kpi-comparison"
import "./datox-compliance-dashboard.css"
import "./widget-editors.css"

class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  render() {
    if (this.state.error)
      return (
        <div className="datox-react-dashboard__fatal">
          <strong>{this.props.labels.somethingWentWrong}</strong>
          <span>{String(this.state.error?.message || this.state.error)}</span>
        </div>
      )
    return this.props.children
  }
}

const WIDGETS = [
  { type: "header", icon: TbLayoutGrid, data: false },
  { type: "text", icon: TbTextCaption, data: false },
  { type: "title", icon: TbTypography, data: false },
  { type: "kpi", icon: TbGauge, data: true },
  { type: "table", icon: TbTable, data: true },
  { type: "freeTable", icon: TbTable, data: false },
  { type: "bar", icon: TbChartBar, data: true },
  { type: "line", icon: TbChartLine, data: true },
  { type: "scatter", icon: TbChartDots, data: true },
  { type: "pie", icon: TbChartPie, data: true },
  { type: "heatmap", icon: TbFlame, data: true },
  { type: "multiSeries", icon: TbChartLine, data: true },
  { type: "treemap", icon: TbLayoutGrid, data: true },
  { type: "funnel", icon: TbFilter, data: true },
  { type: "filter", icon: TbFilter, data: true },
]

const CELL_COLOR_OPERATOR_OPTIONS = [
  { value: "equals", label: "=" },
  { value: "not_equals", label: "!=" },
  { value: "contains", label: "contains" },
  { value: "gt", label: ">" },
  { value: "gte", label: ">=" },
  { value: "lt", label: "<" },
  { value: "lte", label: "<=" },
]

const CHART_CONTROLS = {
  bar: ["orientation", "legend", "values", "axes", "grid", "colors", "stack", "titles", "format"],
  line: ["legend", "values", "axes", "grid", "stack", "titles", "format"],
  multiSeries: ["legend", "values", "axes", "grid", "stack", "titles", "format"],
  scatter: ["axes", "grid", "titles", "format"],
  pie: ["legend", "values", "adjacent", "format"],
  treemap: [],
  funnel: ["legend", "values"],
  heatmap: ["values"],
}

const DashboardTableContext = React.createContext({ loading: false })

function freeStyleFromTable(table, labels) {
  const headers = table?.headers?.length
    ? table.headers
    : [1, 2].map((count) => labels.columnName.replace("{count}", String(count)))
  const rows = table?.rows?.length
    ? table.rows.map((row) => headers.map((header) => row[header] ?? ""))
    : [headers.map(() => "")]
  return {
    headers,
    rows,
    columnWidths: headers.map(() => 168),
    columnTypes: headers.map(() => "text"),
    rowTypes: rows.map(() => "default"),
    showRowNumbers: false,
    highlightHeaderRow: true,
    cellAlign: {},
  }
}

export function DatoxComplianceDashboard(props) {
  return (
    <DashboardErrorBoundary labels={props.labels}>
      <DashboardKpiContext.Provider
        value={{
          datasets: props.kpiDatasets || {},
          locale: props.locale,
          onEvaluateKpi: props.onEvaluateKpi,
          kpiValues: props.kpiValues || {},
          dashboardID: props.selectedDashboardID,
          sourceKey: props.sourceKey || props.selectedDashboardID,
          sourceKeys: props.kpiSourceKeys,
        }}
      >
        <DashboardTableContext.Provider value={{ loading: props.loadingTables, onRetry: props.onRetryDataset }}>
          <DatoxComplianceDashboardContent {...props} />
        </DashboardTableContext.Provider>
      </DashboardKpiContext.Provider>
    </DashboardErrorBoundary>
  )
}

export function DatoxPublishedDashboard(props) {
  return (
    <DashboardKpiContext.Provider
      value={{
        datasets: Object.fromEntries(
          (props.outputTables || []).map((table) => [table.datasetID, { status: "ready", table }]),
        ),
        locale: props.locale,
        onEvaluateKpi: props.onEvaluateKpi,
        kpiValues: props.kpiValues || {},
        sourceKey: props.sourceKey || props.dashboardID || props.selectedDashboardID,
      }}
    >
      <DatoxPublishedDashboardContent {...props} />
    </DashboardKpiContext.Provider>
  )
}

function DashboardSaveStatus({ store, labels }) {
  const source = store || EMPTY_SAVE_STORE
  const status = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot)
  const [saving, dirty] = status.split(":").map(Number)
  return <span>{saving || dirty ? labels.saving : labels.allChangesSaved}</span>
}

function DashboardCreateButton({ store, label, onClick }) {
  const source = store || EMPTY_SAVE_STORE
  const status = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot)
  return (
    <button
      className="datox-react-dashboard__button"
      type="button"
      onClick={onClick}
      disabled={status.startsWith("1:")}
    >
      {label}
    </button>
  )
}

const EMPTY_SAVE_STORE = {
  subscribe: () => () => {},
  getSnapshot: () => "0:0",
}

function DatoxPublishedDashboardContent(props) {
  const [activeTabId, setActiveTabId] = useState(props.config?.tabs?.[0]?.id || "")
  const [filterSelections, setFilterSelections] = useState({})
  const [sourceWidget, setSourceWidget] = useState(null)
  const labels = props.labels
  const config = props.config || defaultConfig(labels.defaultTabName, labels.defaultTabName)
  const activeTab = config.tabs?.find((tab) => tab.id === activeTabId) || config.tabs?.[0]
  const widgets = (activeTab?.widgets || []).map((widget) =>
    widget.type === "filter"
      ? {
          ...widget,
          data: {
            ...(widget.data || {}),
            selectedValues: filterSelections[widget.id] ?? widget.data?.selectedValues ?? [],
          },
        }
      : widget,
  )
  const filterWidgets = widgets.filter((widget) => widget.type === "filter")
  const activeFilterWidgets = widgets.filter(
    (widget) => widget.type === "filter" && widget.data?.selectedValues?.length,
  )
  const comparisonState = useDashboardKpiComparisons({
    projectID: props.projectID,
    dashboardID: props.selectedDashboardID,
    revision: props.dashboards?.find((dashboard) => dashboard.id === props.selectedDashboardID)?.revision ?? 0,
    widgets,
    outputTables: props.outputTables,
    activeFilterWidgets,
    labels,
    enabled: true,
  })
  useEffect(() => setFilterSelections({}), [props.config])
  const toggleFilterValue = (widget, value) => {
    setFilterSelections((current) => {
      const selectedValues = current[widget.id] ?? widget.data?.selectedValues ?? []
      return { ...current, [widget.id]: nextFilterSelectedValues(widget, value, selectedValues) }
    })
  }
  return (
    <div className="datox-react-dashboard datox-react-dashboard--published">
      <div className="datox-react-dashboard__published-tabs">
        {(config.tabs || []).map((tab) => (
          <button
            key={tab.id}
            className={
              tab.id === activeTab?.id
                ? "datox-react-dashboard__published-tab datox-react-dashboard__published-tab--active"
                : "datox-react-dashboard__published-tab"
            }
            type="button"
            onClick={() => setActiveTabId(tab.id)}
          >
            {tab.name || labels.defaultTabName}
          </button>
        ))}
      </div>
      <div className="datox-react-dashboard__published-canvas">
        <div
          className={`datox-react-dashboard__page ${config.design?.lightGrayBackground ? "datox-react-dashboard__page--gray" : ""}`}
          style={{
            "--ds-accent": config.design?.accent || "#2f80ed",
            "--ds-header-color": config.design?.headerColor || "#101828",
            "--ds-heading-font": designFont(config.design?.headingFont),
            "--ds-body-font": designFont(config.design?.bodyFont),
            width: config.canvas?.width || 1123,
            minHeight: dashboardKpiPageHeight(config.canvas?.height, widgets),
          }}
        >
          {widgets.map((widget) => (
            <div
              key={widget.id}
              style={{
                position: "absolute",
                left: widget.layout.x,
                top: widget.layout.y,
                width: widget.layout.w,
                height: dashboardWidgetHeight(widget),
              }}
            >
              <WidgetFrame
                preview
                filterWidgets={filterWidgets}
                labels={labels}
                widget={widget}
                table={applyDashboardFilters(
                  props.outputTables.find((table) => table.datasetID === widget.data?.dataset_id),
                  widget,
                  activeFilterWidgets,
                )}
                accent={config.design?.accent || "#2f80ed"}
                design={config.design || {}}
                selected={false}
                menuOpen={false}
                onToggleMenu={() => {}}
                onRename={() => {}}
                onEditData={() => {}}
                onEditDesign={() => {}}
                onExportTable={() => {}}
                onFullView={() => {}}
                onFilterValueToggle={(value) => toggleFilterValue(widget, value)}
                onDelete={() => {}}
                onDuplicate={() => {}}
                comparison={comparisonState.itemsByWidgetId[widget.id]}
                onExplain={() => setSourceWidget(widget)}
              />
            </div>
          ))}
          {sourceWidget ? (
            <KpiSourcePanel
              widget={sourceWidget}
              filterWidgets={filterWidgets}
              labels={labels}
              onClose={() => setSourceWidget(null)}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function DatoxComplianceDashboardContent(props) {
  const [createOpen, setCreateOpen] = useState(false)
  const [renameState, setRenameState] = useState(null)
  const [deleteDashboardOpen, setDeleteDashboardOpen] = useState(false)
  const [publishedLink, setPublishedLink] = useState("")
  const [publishedLinkCopied, setPublishedLinkCopied] = useState(false)
  const [shareAccessOpen, setShareAccessOpen] = useState(false)
  const [clientAccessOpen, setClientAccessOpen] = useState(false)
  const [shareReviewers, setShareReviewers] = useState([
    { id: "reviewer-1", name: "", email: "", clientValue: "", selected: true },
  ])
  const [shareAccessSending, setShareAccessSending] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false)
  const [exportingSnapshot, setExportingSnapshot] = useState(false)
  const [snapshotOptionsOpen, setSnapshotOptionsOpen] = useState(false)
  const [snapshotTabIds, setSnapshotTabIds] = useState([])
  const [snapshotFullTables, setSnapshotFullTables] = useState(false)
  const [sourceExplanation, setSourceExplanation] = useState(null)
  const sourceExplanationRequestRef = useRef({ id: 0, controller: null })
  const sourceExplanationCacheRef = useRef(new Map())
  useEffect(() => () => sourceExplanationRequestRef.current.controller?.abort(), [])
  const [exportingPdf, setExportingPdf] = useState(false)
  const [exportError, setExportError] = useState("")
  const [newName, setNewName] = useState("")
  const [panel, setPanel] = useState("widgets")
  const [leftPanelOpen, setLeftPanelOpen] = useState(true)
  const [search, setSearch] = useState("")
  const [widgetLibraryView, setWidgetLibraryView] = useState("list")
  const [activeTabId, setActiveTabId] = useState(null)
  const [activeWidgetId, setActiveWidgetId] = useState(null)
  const [selectedWidgetIds, setSelectedWidgetIds] = useState([])
  const [editorWidgetId, setEditorWidgetId] = useState(null)
  const [sourceWidget, setSourceWidget] = useState(null)
  const [widgetEditorPanel, setWidgetEditorPanel] = useState("data")
  const [inspectorWidth, setInspectorWidth] = useState(320)
  const [fullViewWidgetId, setFullViewWidgetId] = useState(null)
  const [inlineEditWidgetId, setInlineEditWidgetId] = useState(null)
  const [dragEnabled, setDragEnabled] = useState(true)
  const [snapEnabled, setSnapEnabled] = useState(() => {
    try {
      return window.localStorage.getItem("datox-dashboard-snap") !== "false"
    } catch {
      return true
    }
  })
  const [draggingWidgetType, setDraggingWidgetType] = useState("")
  const [dashboardMenuOpen, setDashboardMenuOpen] = useState(false)
  const [exportMenuOpen, setExportMenuOpen] = useState(false)
  const [widgetMenuId, setWidgetMenuId] = useState(null)
  const [tabMenuId, setTabMenuId] = useState(null)
  const [historyVersion, setHistoryVersion] = useState(0)
  const [draftConfig, setDraftConfig] = useState(
    cloneDashboardConfig(props.config || defaultConfig(props.labels.defaultTabName, props.labels.defaultTabName)),
  )
  const [cellColorRulesOpen, setCellColorRulesOpen] = useState(false)
  const [cellColorRuleDraft, setCellColorRuleDraft] = useState(null)
  const pageRef = useRef(null)
  const marqueeRef = useRef(null)
  const verticalGuideRef = useRef(null)
  const horizontalGuideRef = useRef(null)
  const widgetRefs = useRef(new Map())
  const dragStateRef = useRef(null)
  const resizeInvalidRef = useRef(false)
  const clipboardRef = useRef([])
  const undoStackRef = useRef([])
  const redoStackRef = useRef([])
  const labels = props.labels
  const config = draftConfig
  const activeTab =
    config.tabs?.find((tab) => tab.id === activeTabId) ||
    config.tabs?.[0] ||
    defaultConfig(config.title || labels.defaultTabName, labels.defaultTabName).tabs[0]
  const widgets = activeTab.widgets || []
  const selectedTable = useMemo(
    () => props.outputTables.find((table) => table.datasetID === props.selectedDatasetID) || props.outputTables[0],
    [props.outputTables, props.selectedDatasetID],
  )
  const selectedDashboard = useMemo(
    () => props.dashboards?.find((dashboard) => dashboard.id === props.selectedDashboardID),
    [props.dashboards, props.selectedDashboardID],
  )
  const selectedDataset = useMemo(
    () => props.datasets.find((dataset) => dataset.id === props.selectedDatasetID) || props.datasets[0],
    [props.datasets, props.selectedDatasetID],
  )
  const paletteItems = useMemo(() => {
    const query = search.trim().toLowerCase()
    return query ? WIDGETS.filter((item) => widgetLabel(labels, item.type).toLowerCase().includes(query)) : WIDGETS
  }, [labels, search])

  useEffect(() => {
    if (!props.selectedDatasetID && selectedTable) props.onSelectDataset?.(selectedTable.datasetID)
  }, [props, selectedTable])

  useEffect(() => {
    setDraftConfig(cloneDashboardConfig(props.config || defaultConfig(labels.defaultTabName, labels.defaultTabName)))
    undoStackRef.current = []
    redoStackRef.current = []
    setHistoryVersion((value) => value + 1)
    setActiveWidgetId(null)
    setSelectedWidgetIds([])
  }, [props.configSyncKey, props.selectedDashboardID])

  useEffect(() => {
    try {
      window.localStorage.setItem("datox-dashboard-snap", String(snapEnabled))
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }, [snapEnabled])

  useEffect(() => {
    if (!activeTabId && config.tabs?.[0]?.id) setActiveTabId(config.tabs[0].id)
    if (activeTabId && config.tabs?.length && !config.tabs.some((tab) => tab.id === activeTabId))
      setActiveTabId(config.tabs[0].id)
  }, [activeTabId, config.tabs])

  useEffect(() => {
    const available = new Set(widgets.map((widget) => widget.id))
    setSelectedWidgetIds((ids) => ids.filter((id) => available.has(id)))
    if (activeWidgetId && !available.has(activeWidgetId)) setActiveWidgetId(null)
  }, [activeTab.id, activeWidgetId, widgets])

  useEffect(() => {
    if (!shareAccessOpen) return
    if (!props.dashboardShareReviewers?.length) return
    setShareReviewers(
      props.dashboardShareReviewers.map((reviewer) => ({
        id: widgetIdValue(),
        approvalIds: reviewer.approvalIds || [],
        name: reviewer.name || "",
        email: reviewer.email,
        clientValue: reviewer.clientValue || "",
        selected: true,
        status: reviewer.status === "requested" ? labels.sent : reviewer.status || labels.sent,
      })),
    )
  }, [shareAccessOpen, props.dashboardShareReviewers, labels.sent])

  const updateConfig = (next) => {
    undoStackRef.current = [...undoStackRef.current.slice(-49), cloneDashboardConfig(config)]
    redoStackRef.current = []
    setDraftConfig(next)
    setHistoryVersion((value) => value + 1)
    props.onConfigChange?.(next)
  }
  const updateTabs = (nextTabs) => updateConfig({ ...config, tabs: nextTabs })
  const updateActiveTab = (patch) =>
    updateTabs((config.tabs || [activeTab]).map((tab) => (tab.id === activeTab.id ? { ...tab, ...patch } : tab)))
  const updateWidgets = (nextWidgets) =>
    updateConfig({
      ...config,
      canvas: {
        ...(config.canvas || {}),
        height: canvasAutoHeight(config.canvas?.height || 794, nextWidgets),
      },
      tabs: (config.tabs || [activeTab]).map((tab) =>
        tab.id === activeTab.id ? { ...tab, widgets: nextWidgets } : tab,
      ),
    })
  const selectedWidget = widgets.find((widget) => widget.id === activeWidgetId)
  const editorWidget = widgets.find((widget) => widget.id === editorWidgetId)
  const fullViewWidget = widgets.find((widget) => widget.id === fullViewWidgetId)
  const closeSourceExplanation = () => {
    sourceExplanationRequestRef.current.id += 1
    sourceExplanationRequestRef.current.controller?.abort()
    sourceExplanationRequestRef.current.controller = null
    setSourceExplanation(null)
  }
  const loadSourceExplanation = async (request, refresh = false) => {
    const cacheKey = JSON.stringify([
      props.configSyncKey,
      request.dashboardID,
      request.tabID,
      request.widgetID,
      request.widgetData,
    ])
    const cached = sourceExplanationCacheRef.current.get(cacheKey)
    if (!refresh && cached?.expiresAt > Date.now()) {
      setSourceExplanation({ loading: false, error: "", data: cached.data, request })
      return
    }
    const id = sourceExplanationRequestRef.current.id + 1
    sourceExplanationRequestRef.current.controller?.abort()
    const controller = new AbortController()
    sourceExplanationRequestRef.current = { id, controller }
    setSourceExplanation({ loading: true, error: "", data: null, request })
    let timedOut = false
    const timeout = window.setTimeout(() => {
      timedOut = true
      controller.abort()
    }, 90_000)
    try {
      const data = await props.onExplainSource(request, controller.signal)
      if (sourceExplanationRequestRef.current.id === id) {
        sourceExplanationCacheRef.current.set(cacheKey, { data, expiresAt: Date.now() + 5 * 60_000 })
        if (sourceExplanationCacheRef.current.size > 50)
          sourceExplanationCacheRef.current.delete(sourceExplanationCacheRef.current.keys().next().value)
        setSourceExplanation({ loading: false, error: "", data, request })
      }
    } catch (error) {
      if (sourceExplanationRequestRef.current.id === id && (timedOut || error?.name !== "AbortError"))
        setSourceExplanation({ loading: false, error: labels.requestFailed, data: null, request })
    } finally {
      window.clearTimeout(timeout)
      if (sourceExplanationRequestRef.current.id === id) sourceExplanationRequestRef.current.controller = null
    }
  }

  const addWidget = (type, position) => {
    const widget = createWidget(type, widgets.length, selectedTable, labels, position)
    widget.layout = nearestFreePlacement(widget.layout, widgets, config.canvas?.width || 1123, snapEnabled ? 4 : 1)
    updateWidgets([...widgets, widget])
    setActiveWidgetId(widget.id)
    setSelectedWidgetIds([widget.id])
  }

  const addTab = () => {
    const nextIndex = (config.tabs?.length || 0) + 1
    const tab = { id: `tab-${Date.now()}`, name: labels.newTabName.replace("{count}", String(nextIndex)), widgets: [] }
    updateTabs([...(config.tabs || []), tab])
    setActiveTabId(tab.id)
    setActiveWidgetId(null)
    setSelectedWidgetIds([])
  }
  const duplicateTab = (tab) => {
    const copy = {
      ...cloneDashboardConfig(tab),
      id: `tab-${Date.now()}`,
      name: `${tab.name || labels.defaultTabName} ${labels.copySuffix}`,
      widgets: (tab.widgets || []).map((widget) => ({ ...cloneDashboardConfig(widget), id: widgetIdValue() })),
    }
    updateTabs([...(config.tabs || []), copy])
    setActiveTabId(copy.id)
    setTabMenuId(null)
    setActiveWidgetId(null)
    setSelectedWidgetIds([])
    setEditorWidgetId(null)
  }
  const deleteTab = (tabId) => {
    if ((config.tabs || []).length <= 1) return
    const nextTabs = (config.tabs || []).filter((tab) => tab.id !== tabId)
    updateTabs(nextTabs)
    if (activeTab.id === tabId) setActiveTabId(nextTabs[0]?.id ?? null)
    setTabMenuId(null)
    setActiveWidgetId(null)
    setSelectedWidgetIds([])
    setEditorWidgetId(null)
  }

  const openRenameDashboard = () =>
    setRenameState({ kind: "dashboard", title: labels.renameDashboard, name: config.title || labels.dashboardTitle })
  const openRenameTab = (tab) =>
    setRenameState({ kind: "tab", tabId: tab.id, title: labels.renameTab, name: tab.name || labels.defaultTabName })
  const openRenameWidget = (widget) =>
    setRenameState({
      kind: "widget",
      widgetId: widget.id,
      title: labels.renameWidget,
      name: widget.title || widgetLabel(labels, widget.type),
    })
  const submitRename = (event) => {
    event.preventDefault()
    const current = renameState
    const name = current?.name?.trim()
    if (!name) return
    if (current.kind === "dashboard") updateConfig({ ...config, title: name })
    if (current.kind === "tab")
      updateTabs((config.tabs || []).map((tab) => (tab.id === current.tabId ? { ...tab, name } : tab)))
    if (current.kind === "widget") updateWidget(current.widgetId, { title: name })
    setRenameState(null)
  }

  const updateWidget = (widgetId, patch) => {
    updateWidgets(widgets.map((widget) => (widget.id === widgetId ? { ...widget, ...patch } : widget)))
  }

  const deleteWidget = (widgetId) => {
    updateWidgets(widgets.filter((widget) => widget.id !== widgetId))
    if (activeWidgetId === widgetId) setActiveWidgetId(null)
    setSelectedWidgetIds((ids) => ids.filter((id) => id !== widgetId))
    if (editorWidgetId === widgetId) setEditorWidgetId(null)
    if (fullViewWidgetId === widgetId) setFullViewWidgetId(null)
  }

  const duplicateWidget = (widgetId) => {
    const source = widgets.find((widget) => widget.id === widgetId)
    if (!source) return
    const copy = {
      ...cloneDashboardConfig(source),
      id: widgetIdValue(),
      title: `${source.title} ${labels.copySuffix}`,
      layout: nearestFreePlacement(
        { ...source.layout, x: source.layout.x + 28, y: source.layout.y + 28 },
        widgets,
        config.canvas?.width || 1123,
        snapEnabled ? 4 : 1,
      ),
    }
    updateWidgets([...widgets, copy])
    setActiveWidgetId(copy.id)
    setSelectedWidgetIds([copy.id])
  }
  const duplicateSelectedWidgets = () => {
    const sources = widgets.filter((widget) => selectedWidgetIds.includes(widget.id))
    const canvasWidth = config.canvas?.width || 1123
    const offset = Array.from({ length: 40 }, (_, index) => ({
      x: 28 * ((index % 4) + 1),
      y: 28 * (Math.floor(index / 4) + 1),
    })).find((candidate) =>
      sources.every((source) => {
        const layout = { ...source.layout, x: source.layout.x + candidate.x, y: source.layout.y + candidate.y }
        return layout.x >= 0 && layout.x + layout.w <= canvasWidth && !placementCollides(layout, widgets, [])
      }),
    ) || { x: 28, y: 28 }
    const copies = sources.map((source) => ({
      ...cloneDashboardConfig(source),
      id: widgetIdValue(),
      title: `${source.title} ${labels.copySuffix}`,
      layout: { ...source.layout, x: source.layout.x + offset.x, y: source.layout.y + offset.y },
    }))
    if (!copies.length) return
    updateWidgets([...widgets, ...copies])
    setSelectedWidgetIds(copies.map((widget) => widget.id))
    setActiveWidgetId(copies.at(-1).id)
  }
  const deleteSelectedWidgets = () => {
    if (!selectedWidgetIds.length) return
    updateWidgets(widgets.filter((widget) => !selectedWidgetIds.includes(widget.id)))
    setSelectedWidgetIds([])
    setActiveWidgetId(null)
    setEditorWidgetId(null)
  }

  const setOrientation = (orientation) => {
    const oldWidth = config.canvas?.width || 1123
    const oldHeight = config.canvas?.height || 794
    const nextCanvas =
      orientation === "portrait" ? { orientation, width: 794, height: 1123 } : { orientation, width: 1123, height: 794 }
    const xRatio = nextCanvas.width / oldWidth
    const yRatio = nextCanvas.height / oldHeight
    updateConfig({
      ...config,
      canvas: nextCanvas,
      tabs: (config.tabs || []).map((tab) => ({
        ...tab,
        widgets: (tab.widgets || []).map((widget) => ({
          ...widget,
          layout: {
            x: Math.round(widget.layout.x * xRatio),
            y: Math.round(widget.layout.y * yRatio),
            w: Math.max(80, Math.round(widget.layout.w * xRatio)),
            h: Math.max(48, Math.round(widget.layout.h * yRatio)),
          },
        })),
      })),
    })
  }
  const tidyLayout = () => {
    const canvasWidth = config.canvas?.width || 1123
    const gap = 24
    const margin = 24
    const contentWidth = canvasWidth - margin * 2
    const twoColumnWidth = Math.floor((contentWidth - gap) / 2)
    const threeColumnWidth = Math.floor((contentWidth - gap * 2) / 3)
    const fourColumnWidth = Math.floor((contentWidth - gap * 3) / 4)
    const sortedWidgets = [...widgets].sort(
      (first, second) => first.layout.y - second.layout.y || first.layout.x - second.layout.x,
    )
    const headers = sortedWidgets.filter((widget) => widget.type === "header")
    const titles = sortedWidgets.filter((widget) => widget.type === "title")
    const kpis = sortedWidgets.filter((widget) => widget.type === "kpi")
    const filters = sortedWidgets.filter((widget) => widget.type === "filter")
    const charts = sortedWidgets.filter((widget) => isChartWidget(widget.type))
    const tables = sortedWidgets.filter((widget) => widget.type === "table" || widget.type === "freeTable")
    const texts = sortedWidgets.filter((widget) => widget.type === "text")
    const placedIds = new Set(
      [...headers, ...titles, ...kpis, ...filters, ...charts, ...tables, ...texts].map((widget) => widget.id),
    )
    const others = sortedWidgets.filter((widget) => !placedIds.has(widget.id))
    const state = { y: margin, widgets: [] }

    const placeFullWidth = (widget, height) => {
      state.widgets.push({
        ...widget,
        layout: {
          ...widget.layout,
          x: widget.type === "header" ? 0 : margin,
          y: state.y,
          w: widget.type === "header" ? canvasWidth : contentWidth,
          h: height,
        },
      })
      state.y += height + gap
    }
    const placeGrid = (group, columns, width, height) => {
      group.forEach((widget, index) => {
        const column = index % columns
        const row = Math.floor(index / columns)
        state.widgets.push({
          ...widget,
          layout: {
            ...widget.layout,
            x: margin + column * (width + gap),
            y: state.y + row * (height + gap),
            w: width,
            h: height,
          },
        })
      })
      if (group.length) state.y += Math.ceil(group.length / columns) * (height + gap)
    }

    headers.forEach((widget) => placeFullWidth(widget, Math.max(96, Math.min(widget.layout.h || 96, 140))))
    titles.forEach((widget) => placeFullWidth(widget, Math.max(64, Math.min(widget.layout.h || 72, 110))))
    placeGrid(kpis, canvasWidth >= 1000 ? 4 : 2, canvasWidth >= 1000 ? fourColumnWidth : twoColumnWidth, 150)
    placeGrid(filters, canvasWidth >= 1000 ? 3 : 2, canvasWidth >= 1000 ? threeColumnWidth : twoColumnWidth, 120)
    placeGrid(charts, canvasWidth >= 900 ? 2 : 1, canvasWidth >= 900 ? twoColumnWidth : contentWidth, 300)
    tables.forEach((widget) => placeFullWidth(widget, Math.max(300, Math.min(widget.layout.h || 320, 420))))
    placeGrid(texts, canvasWidth >= 900 ? 2 : 1, canvasWidth >= 900 ? twoColumnWidth : contentWidth, 150)
    placeGrid(others, canvasWidth >= 900 ? 2 : 1, canvasWidth >= 900 ? twoColumnWidth : contentWidth, 180)

    updateConfig({
      ...config,
      canvas: { ...(config.canvas || {}), height: Math.max(config.canvas?.height || 794, state.y + margin) },
      tabs: (config.tabs || []).map((tab) => (tab.id === activeTab.id ? { ...tab, widgets: state.widgets } : tab)),
    })
    setActiveWidgetId(null)
    setSelectedWidgetIds([])
    setEditorWidgetId(null)
  }

  const undo = () => {
    const previous = undoStackRef.current.at(-1)
    if (!previous) return
    undoStackRef.current = undoStackRef.current.slice(0, -1)
    redoStackRef.current = [...redoStackRef.current, cloneDashboardConfig(config)]
    setDraftConfig(previous)
    setHistoryVersion((value) => value + 1)
    props.onConfigChange?.(previous)
  }

  const redo = () => {
    const next = redoStackRef.current.at(-1)
    if (!next) return
    redoStackRef.current = redoStackRef.current.slice(0, -1)
    undoStackRef.current = [...undoStackRef.current, cloneDashboardConfig(config)]
    setDraftConfig(next)
    setHistoryVersion((value) => value + 1)
    props.onConfigChange?.(next)
  }

  const updateEditorWidgetData = (patch) => {
    if (!editorWidget) return
    updateWidget(editorWidget.id, { data: { ...(editorWidget.data || {}), ...patch } })
  }
  const changeWidgetType = (type, title) => {
    if (!editorWidget) return
    const oldChart = isChartWidget(editorWidget.type)
    const newChart = isChartWidget(type)
    const columns = editorWidgetColumns
    const numeric = editorWidgetNumericColumns
    const data = { ...(editorWidget.data || {}) }
    if (newChart && !oldChart) {
      data.label = columns.includes(data.label) ? data.label : columns[0]
      data.metric = numeric.includes(data.metric) ? data.metric : numeric[0]
      if (!["sum", "avg", "min", "max", "count"].includes(data.aggregation)) data.aggregation = "sum"
    }
    if (type === "scatter") {
      data.xColumn = numeric.includes(data.xColumn)
        ? data.xColumn
        : numeric.find((column) => column !== data.metric) || ""
      data.yColumn = numeric.includes(data.yColumn) ? data.yColumn : data.metric || ""
      data.sizeColumn = numeric.includes(data.sizeColumn) ? data.sizeColumn : ""
    }
    if (
      type === "freeTable" &&
      !data.freeStyle &&
      editorWidgetConversionTable &&
      !editorWidgetConversionTable.loadError &&
      editorWidgetConversionTable.headers.length <= 20 &&
      editorWidgetConversionTable.rows.length <= 50
    )
      data.freeStyle = freeStyleFromTable(editorWidgetConversionTable, labels)
    if (type === "freeTable" && data.freeStyle) delete data.dataset_id
    if (type === "table" && !data.dataset_id) data.dataset_id = selectedTable?.datasetID
    updateWidget(editorWidget.id, {
      type,
      ...(title ? { title } : {}),
      data,
      ...(type !== "kpi" && editorWidget.kpi_calculation ? { kpi_calculation: null } : {}),
    })
  }
  const addEditorFilter = () => {
    if (!editorWidget) return
    updateEditorWidgetData({
      filters: [
        ...(editorWidget.data?.filters || []),
        { id: widgetIdValue(), column: editorWidgetColumns[0] || "", operator: "contains", value: "" },
      ],
    })
  }
  const toggleMeasure = (column) => {
    if (!editorWidget) return
    const measures = editorWidget.data?.measures || []
    updateEditorWidgetData({
      measures: measures.includes(column) ? measures.filter((item) => item !== column) : [...measures, column],
    })
  }
  const updateEditorFilter = (filterId, patch) => {
    if (!editorWidget) return
    updateEditorWidgetData({
      filters: (editorWidget.data?.filters || []).map((filter) =>
        filter.id === filterId ? { ...filter, ...patch } : filter,
      ),
    })
  }
  const removeEditorFilter = (filterId) => {
    if (!editorWidget) return
    updateEditorWidgetData({ filters: (editorWidget.data?.filters || []).filter((filter) => filter.id !== filterId) })
  }
  const toggleEditorColumn = (column) => {
    if (!editorWidget) return
    const currentColumns = editorWidget.data?.columns?.length ? editorWidget.data.columns : editorWidgetColumns
    const nextColumns = currentColumns.includes(column)
      ? currentColumns.filter((item) => item !== column)
      : [...currentColumns, column]
    const nextStyle =
      currentColumns.includes(column) && Array.isArray(editorWidget.style?.cellColorRules)
        ? {
            ...(editorWidget.style || {}),
            cellColorRules: editorWidget.style.cellColorRules.filter((rule) => nextColumns.includes(rule.column)),
          }
        : editorWidget.style
    updateWidget(editorWidget.id, {
      data: { ...(editorWidget.data || {}), columns: nextColumns },
      style: nextStyle,
    })
  }

  const editorWidgetTable = editorWidget?.data?.dataset_id
    ? props.outputTables.find((table) => table.datasetID === editorWidget.data.dataset_id)
    : selectedTable
  const editorWidgetNumericColumns =
    editorWidgetTable?.headers?.filter((header) =>
      editorWidgetTable.rows.some((row) => numericCell(row[header]) !== null),
    ) || []
  const editorWidgetColumns = editorWidgetTable?.headers || []
  const editorWidgetVisibleColumns = editorWidget?.data?.columns?.length
    ? editorWidget.data.columns
    : editorWidgetColumns
  const editorCellColorRules = editorWidget?.style?.cellColorRules || []

  const updateEditorWidgetStyle = (patch) => {
    if (!editorWidget) return
    updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), ...patch } })
  }

  const updateEditorCellColorRules = (nextRules) => {
    updateEditorWidgetStyle({ cellColorRules: nextRules })
  }

  const createEditorCellColorRule = (column = "") => ({
    id: widgetIdValue(),
    column,
    operator: "equals",
    value: "",
    color: "#dbeafe",
    highlight_row: false,
  })

  const openEditorCellColorRule = (rule) => {
    setCellColorRuleDraft(rule)
    setCellColorRulesOpen(true)
  }

  const closeEditorCellColorRule = () => {
    setCellColorRulesOpen(false)
    setCellColorRuleDraft(null)
  }

  const saveEditorCellColorRule = () => {
    if (!cellColorRuleDraft?.column || !cellColorRuleDraft?.operator) return
    const existing = editorCellColorRules.some((rule) => rule.id === cellColorRuleDraft.id)
    const nextRules = existing
      ? editorCellColorRules.map((rule) => (rule.id === cellColorRuleDraft.id ? cellColorRuleDraft : rule))
      : [...editorCellColorRules, cellColorRuleDraft]
    updateEditorCellColorRules(nextRules)
    closeEditorCellColorRule()
  }

  const updateEditorCellColorRule = (patch) => {
    setCellColorRuleDraft((current) => (current ? { ...current, ...patch } : current))
  }

  const removeEditorCellColorRule = (ruleId) => {
    updateEditorCellColorRules(editorCellColorRules.filter((rule) => rule.id !== ruleId))
  }

  // const moveEditorCellColorRule = (ruleId, direction) => {
  //   const index = editorCellColorRules.findIndex((rule) => rule.id === ruleId)
  //   if (index < 0) return
  //   const targetIndex = direction === "up" ? index - 1 : index + 1
  //   if (targetIndex < 0 || targetIndex >= editorCellColorRules.length) return
  //   const nextRules = [...editorCellColorRules]
  //   const [rule] = nextRules.splice(index, 1)
  //   nextRules.splice(targetIndex, 0, rule)
  //   updateEditorCellColorRules(nextRules)
  // }

  const clientAccess = config.clientAccess || {}
  const clientDatasetID =
    clientAccess.dataset_id || clientAccess.datasetID || props.selectedDatasetID || props.datasets?.[0]?.id || ""
  const clientColumns = datasetColumns(clientDatasetID, props.outputTables, props.datasets)
  const clientColumn = clientAccess.column || clientColumns[0] || ""
  const clientValues = uniqueColumnValues(
    props.outputTables.find((table) => table.datasetID === clientDatasetID),
    clientColumn,
  )
  const activeFilterWidgets = widgets.filter(
    (widget) => widget.type === "filter" && widget.data?.selectedValues?.length,
  )
  const comparisonState = useDashboardKpiComparisons({
    projectID: props.projectID,
    dashboardID: selectedDashboard?.id || props.selectedDashboardID,
    revision: selectedDashboard?.revision ?? 0,
    widgets,
    outputTables: props.outputTables,
    activeFilterWidgets,
    labels,
    enabled: true,
  })
  const editorWidgetConversionTable = editorWidget
    ? applyWidgetTableSettings(
        applyDashboardFilters(editorWidgetTable, editorWidget, activeFilterWidgets),
        editorWidget.data,
      )
    : null
  const openWidgetEditor = (widget, panelName) => {
    setActiveWidgetId(widget.id)
    setEditorWidgetId(widget.id)
    setWidgetEditorPanel(panelName)
    setWidgetMenuId(null)
  }
  const exportWidgetTable = (widget) => {
    const table = applyWidgetTableSettings(
      applyDashboardFilters(
        props.outputTables.find((item) => item.datasetID === widget.data?.dataset_id),
        widget,
        activeFilterWidgets,
      ),
      widget.data,
    )
    if (!table) return
    const csv = [table.headers, ...table.rows.map((row) => table.headers.map((header) => row[header] ?? ""))]
      .map((row) => row.map(csvCell).join(","))
      .join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${widget.title || widgetLabel(labels, widget.type)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }
  // const exportPdf = async () => {
  //   if (exportingPdf) return
  //   setExportingPdf(true)
  //   setExportError("")
  //   try {
  //     await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
  //     const expected = (config.tabs || [])
  //       .flatMap((tab) => tab.widgets || [])
  //       .filter((widget) => widget.type === "kpi").length
  //     if (!exportRef.current) throw new Error("kpi_export_failed")
  //     await waitForDashboardKpis(exportRef.current, expected)
  //     await document.fonts?.ready
  //     await new Promise((resolve) => requestAnimationFrame(resolve))
  //     const html2canvasModule = await import("html2canvas")
  //     const jsPdfModule = await import("jspdf")
  //     const pages = Array.from(exportRef.current?.querySelectorAll(".datox-react-dashboard__export-page") || [])
  //     if (!pages.length) return
  //     const pdfWidth = config.canvas?.orientation === "portrait" ? 794 : 1123
  //     const pdfHeight = config.canvas?.orientation === "portrait" ? 1123 : 794
  //     const pdf = new jsPdfModule.jsPDF({
  //       orientation: pdfWidth > pdfHeight ? "landscape" : "portrait",
  //       unit: "pt",
  //       format: [pdfWidth, pdfHeight],
  //     })
  //     for (const [index, page] of pages.entries()) {
  //       const sourceWidth = page.offsetWidth || pdfWidth
  //       const sourceHeight = page.offsetHeight || pdfHeight
  //       const canvas = await html2canvasModule.default(page, {
  //         backgroundColor: "#ffffff",
  //         scale: 1.25,
  //         useCORS: true,
  //         width: sourceWidth,
  //         height: sourceHeight,
  //       })
  //       const scale = Math.min(pdfWidth / sourceWidth, pdfHeight / sourceHeight)
  //       const imageWidth = sourceWidth * scale
  //       const imageHeight = sourceHeight * scale
  //       if (index > 0) pdf.addPage([pdfWidth, pdfHeight], pdfWidth > pdfHeight ? "landscape" : "portrait")
  //       pdf.addImage(
  //         canvas.toDataURL("image/jpeg", 0.86),
  //         "JPEG",
  //         (pdfWidth - imageWidth) / 2,
  //         (pdfHeight - imageHeight) / 2,
  //         imageWidth,
  //         imageHeight,
  //         undefined,
  //         "FAST",
  //       )
  //     }
  //     pdf.save(`${config.title || labels.dashboardTitle}.pdf`)
  //   } catch (error) {
  //     setExportError(error.message === "kpi_export_timeout" ? labels.kpi.exportTimeout : labels.kpi.exportFailed)
  //   } finally {
  //     setExportingPdf(false)
  //   }
  // }
  const publishDashboard = async () => {
    const url = await props.onPublishDashboard?.()
    if (url) {
      setPublishedLink(url)
      setPublishedLinkCopied(false)
    }
  }
  const copyPublishedLink = async () => {
    await navigator.clipboard?.writeText(publishedLink)
    setPublishedLinkCopied(true)
    window.setTimeout(() => setPublishedLinkCopied(false), 1800)
  }
  const submitShareAccess = async (event) => {
    event.preventDefault()
    const reviewersToSend = uniqueReviewers(
      shareReviewers.filter(
        (reviewer) => reviewer.selected && reviewer.email.trim() && reviewer.status !== labels.duplicateEmail,
      ),
    )
    if (!reviewersToSend.length || hasDuplicateReviewerEmails(shareReviewers)) return
    const pendingEmails = new Set(reviewersToSend.map((reviewer) => reviewer.email))
    setShareAccessSending(true)
    setShareReviewers((items) =>
      items.map((item) =>
        pendingEmails.has(item.email.trim().toLowerCase()) ? { ...item, status: labels.sendingShareAccess } : item,
      ),
    )
    try {
      const url = await props.onShareAccess?.(reviewersToSend)
      if (url)
        setShareReviewers((items) =>
          items.map((item) =>
            pendingEmails.has(item.email.trim().toLowerCase()) ? { ...item, status: labels.sent } : item,
          ),
        )
      if (!url)
        setShareReviewers((items) =>
          items.map((item) =>
            pendingEmails.has(item.email.trim().toLowerCase()) ? { ...item, status: labels.notSent } : item,
          ),
        )
    } finally {
      setShareAccessSending(false)
    }
  }
  const updateShareReviewer = (id, patch) =>
    setShareReviewers((items) => items.map((item) => (item.id === id ? { ...item, ...patch } : item)))
  const updateShareReviewerEmail = (id, value) => {
    const emails = value
      .split(/[\n,;]/)
      .map((email) => email.trim())
      .filter(Boolean)
    if (emails.length <= 1) {
      const email = value.trim()
      const duplicate =
        email &&
        shareReviewers.some(
          (reviewer) => reviewer.id !== id && reviewer.email.trim().toLowerCase() === email.toLowerCase(),
        )
      updateShareReviewer(id, { email, status: duplicate ? labels.duplicateEmail : labels.notSent })
      return
    }
    setShareReviewers((items) =>
      items.flatMap((item) => {
        if (item.id !== id) return [item]
        return emails.map((email, index) =>
          index === 0
            ? { ...item, email, status: item.status || labels.notSent }
            : { id: widgetIdValue(), name: "", email, clientValue: "", selected: true, status: labels.notSent },
        )
      }),
    )
  }
  const removeShareReviewer = async (reviewer) => {
    if (reviewer.approvalIds?.length) await props.onRemoveShareReviewer?.(reviewer.approvalIds)
    setShareReviewers((items) =>
      items.length <= 1
        ? [{ id: "reviewer-1", name: "", email: "", clientValue: "", selected: true, status: labels.notSent }]
        : items.filter((item) => item.id !== reviewer.id),
    )
  }
  const addShareReviewer = () =>
    setShareReviewers((items) => [
      ...items,
      { id: widgetIdValue(), name: "", email: "", clientValue: "", selected: true, status: labels.notSent },
    ])
  const closeMenusOnOutsideClick = (event) => {
    if (
      event.target.closest(".datox-react-dashboard__menu-wrap") ||
      event.target.closest(".datox-react-dashboard__widget-actions") ||
      event.target.closest(".datox-react-dashboard__tab")
    )
      return
    setDashboardMenuOpen(false)
    setExportMenuOpen(false)
    setWidgetMenuId(null)
    setTabMenuId(null)
  }
  const updateFilterTarget = (datasetID, patch) => {
    if (!editorWidget) return
    const targets = editorWidget.data?.targets || []
    const existing = targets.find((target) => target.dataset_id === datasetID)
    const nextTarget = {
      dataset_id: datasetID,
      column:
        patch.column ?? existing?.column ?? datasetColumns(datasetID, props.outputTables, props.datasets)[0] ?? "",
    }
    updateEditorWidgetData({
      targets: existing
        ? targets.map((target) => (target.dataset_id === datasetID ? { ...target, ...nextTarget } : target))
        : [...targets, nextTarget],
    })
  }
  const removeFilterTarget = (datasetID) => {
    if (!editorWidget) return
    updateEditorWidgetData({
      targets: (editorWidget.data?.targets || []).filter((target) => target.dataset_id !== datasetID),
    })
  }
  const updateClientAccess = (patch) => {
    const nextDatasetID = patch.dataset_id || clientDatasetID
    const nextColumns = datasetColumns(nextDatasetID, props.outputTables, props.datasets)
    updateConfig({
      ...config,
      clientAccess: {
        dataset_id: nextDatasetID,
        column: patch.column ?? (patch.dataset_id ? nextColumns[0] : clientColumn) ?? "",
      },
    })
  }
  const toggleFilterValue = (widget, value) => {
    const selectedValues = widget.data?.selectedValues || []
    updateWidget(widget.id, {
      data: { ...(widget.data || {}), selectedValues: nextFilterSelectedValues(widget, value, selectedValues) },
    })
  }
  const exportDashboardSnapshot = async () => {
    if (exportingSnapshot || !pageRef.current) return
    const tabsToExport = (config.tabs || []).filter((tab) => snapshotTabIds.includes(tab.id))
    if (!tabsToExport.length) return
    setSnapshotOptionsOpen(false)
    setExportingSnapshot(true)
    const previousTabID = activeTab.id
    const previousSelection = selectedWidgetIds
    setSelectedWidgetIds([])
    setActiveWidgetId(null)
    setWidgetMenuId(null)
    try {
      const { toPng } = await import("html-to-image")
      const { jsPDF } = await import("jspdf")
      const captures = []
      const fullTables = []
      for (const tab of tabsToExport) {
        setActiveTabId(tab.id)
        await waitForDashboardPaint()
        const node = pageRef.current
        const width = node.scrollWidth
        const height = node.scrollHeight
        node.classList.add("datox-react-dashboard__page--snapshotting")
        const image = await toPng(node, {
          width,
          height,
          pixelRatio: 2,
          cacheBust: true,
          backgroundColor: config.design?.lightGrayBackground ? "#f8fafc" : "#ffffff",
          filter: (element) =>
            !element?.classList?.contains("datox-react-dashboard__widget-actions") &&
            !element?.classList?.contains("datox-react-dashboard__alignment-guide") &&
            !element?.classList?.contains("datox-react-dashboard__marquee") &&
            !element?.classList?.contains("datox-react-dashboard__selection-toolbar") &&
            !element?.classList?.contains("datox-react-dashboard__text-drag-strip"),
        }).finally(() => node.classList.remove("datox-react-dashboard__page--snapshotting"))
        captures.push({ width, height, image })
      }
      if (snapshotFullTables) {
        for (const tab of tabsToExport) {
          const tabWidgets = tab.widgets || []
          const filters = tabWidgets.filter((widget) => widget.type === "filter" && widget.data?.selectedValues?.length)
          for (const widget of tabWidgets.filter((item) => item.type === "table" || item.type === "freeTable")) {
            const table = applyWidgetTableSettings(
              applyDashboardFilters(
                props.outputTables.find((item) => item.datasetID === widget.data?.dataset_id),
                widget,
                filters,
              ),
              { ...(widget.data || {}), rowLimit: undefined },
            )
            if (!table) continue
            fullTables.push({ title: widget.title || widgetLabel(labels, widget.type), table })
          }
        }
      }
      if (!captures.length) return
      const first = captures[0]
      const pdf = new jsPDF({
        orientation: first.width >= first.height ? "landscape" : "portrait",
        unit: "px",
        format: [first.width, first.height],
        hotfixes: ["px_scaling"],
        compress: true,
      })
      captures.forEach((capture, index) => {
        if (index > 0)
          pdf.addPage([capture.width, capture.height], capture.width >= capture.height ? "landscape" : "portrait")
        pdf.addImage(
          capture.image,
          "PNG",
          0,
          0,
          pdf.internal.pageSize.getWidth(),
          pdf.internal.pageSize.getHeight(),
          undefined,
          "FAST",
        )
      })
      if (fullTables.length) {
        const { default: autoTable } = await import("jspdf-autotable")
        fullTables.forEach(({ title, table }) => {
          pdf.addPage([first.width, first.height], first.width >= first.height ? "landscape" : "portrait")
          const pageWidth = pdf.internal.pageSize.getWidth()
          autoTable(pdf, {
            head: [table.headers],
            body: table.rows.map((row) => table.headers.map((header) => String(row[header] ?? ""))),
            startY: 36,
            margin: { top: 36, right: 0, bottom: 12, left: 0 },
            tableWidth: pageWidth,
            theme: "grid",
            styles: {
              fontSize: table.headers.length > 12 ? 5.5 : table.headers.length > 8 ? 7 : 9,
              cellPadding: table.headers.length > 8 ? 2 : 4,
              overflow: "linebreak",
              valign: "middle",
            },
            headStyles: {
              fillColor: [33, 119, 146],
              textColor: [255, 255, 255],
              fontStyle: "bold",
              cellPadding: table.headers.length > 8 ? 2 : 4,
            },
            didDrawPage: () => {
              pdf.setFontSize(14)
              pdf.setTextColor(16, 24, 40)
              pdf.text(title, 8, 24)
            },
          })
        })
      }
      pdf.save(`${config.title || labels.dashboardTitle}.pdf`)
    } finally {
      setActiveTabId(previousTabID)
      setSelectedWidgetIds(previousSelection)
      setExportingSnapshot(false)
    }
  }
  const selectWidget = (widgetId, event) => {
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      setSelectedWidgetIds((ids) => (ids.includes(widgetId) ? ids.filter((id) => id !== widgetId) : [...ids, widgetId]))
      setActiveWidgetId(widgetId)
      return
    }
    if (selectedWidgetIds.includes(widgetId)) {
      setActiveWidgetId(widgetId)
      return
    }
    setSelectedWidgetIds([widgetId])
    setActiveWidgetId(widgetId)
  }
  const hideGuides = () => {
    if (verticalGuideRef.current) verticalGuideRef.current.style.display = "none"
    if (horizontalGuideRef.current) horizontalGuideRef.current.style.display = "none"
  }
  const setCollisionPreview = (ids, invalid) => {
    ids.forEach((id) =>
      widgetRefs.current
        .get(id)
        ?.resizableElement?.current?.classList.toggle("datox-react-dashboard__rnd--collision", invalid),
    )
  }
  const previewGroupDrag = (widget, data) => {
    const state = dragStateRef.current
    if (!state) return
    const grid = snapEnabled ? 4 : 1
    const others = widgets.filter((item) => !state.ids.includes(item.id))
    const proposed = {
      ...widget.layout,
      x: snapValue(data.x, grid),
      y: snapValue(data.y, grid),
    }
    const guides = alignmentGuides(proposed, others, 4)
    const requested = { x: guides.x - widget.layout.x, y: guides.y - widget.layout.y }
    const delta = boundedGroupMove(state.widgets, requested, {
      width: config.canvas?.width || 1123,
      height: Math.max(config.canvas?.height || 794, canvasAutoHeight(config.canvas?.height || 794, widgets)),
    })
    const layouts = state.widgets.map((item) => ({
      ...item.layout,
      x: item.layout.x + delta.x,
      y: item.layout.y + delta.y,
    }))
    const invalid = layouts.some((layout) => placementCollides(layout, widgets, state.ids))
    state.delta = delta
    state.invalid = invalid
    state.widgets.forEach((item, index) => {
      if (item.id === state.activeId) return
      const element = widgetRefs.current.get(item.id)?.resizableElement?.current
      if (element) element.style.transform = `translate(${layouts[index].x}px, ${layouts[index].y}px)`
    })
    setCollisionPreview(state.ids, invalid)
    if (verticalGuideRef.current) {
      verticalGuideRef.current.style.display = guides.vertical == null ? "none" : "block"
      verticalGuideRef.current.style.left = `${guides.vertical || 0}px`
    }
    if (horizontalGuideRef.current) {
      horizontalGuideRef.current.style.display = guides.horizontal == null ? "none" : "block"
      horizontalGuideRef.current.style.top = `${guides.horizontal || 0}px`
    }
  }
  const finishGroupDrag = () => {
    const state = dragStateRef.current
    if (!state) return
    setCollisionPreview(state.ids, false)
    hideGuides()
    dragStateRef.current = null
    if (state.invalid) {
      state.widgets.forEach((item) => {
        const instance = widgetRefs.current.get(item.id)
        if (item.id === state.activeId) instance?.updatePosition(item.layout)
        const element = instance?.resizableElement?.current
        if (element) element.style.transform = `translate(${item.layout.x}px, ${item.layout.y}px)`
      })
      return
    }
    updateWidgets(
      widgets.map((widget) =>
        state.ids.includes(widget.id)
          ? {
              ...widget,
              layout: { ...widget.layout, x: widget.layout.x + state.delta.x, y: widget.layout.y + state.delta.y },
            }
          : widget,
      ),
    )
  }
  const startMarquee = (event) => {
    if (event.button !== 0 || event.target !== event.currentTarget) return
    const rect = pageRef.current?.getBoundingClientRect()
    if (!rect) return
    const start = { x: event.clientX - rect.left, y: event.clientY - rect.top }
    if (!event.shiftKey && !event.ctrlKey && !event.metaKey) {
      setSelectedWidgetIds([])
      setActiveWidgetId(null)
    }
    const move = (pointerEvent) => {
      const current = { x: pointerEvent.clientX - rect.left, y: pointerEvent.clientY - rect.top }
      const marquee = {
        x: Math.max(0, Math.min(start.x, current.x)),
        y: Math.max(0, Math.min(start.y, current.y)),
        w: Math.abs(current.x - start.x),
        h: Math.abs(current.y - start.y),
      }
      if (marqueeRef.current) {
        Object.assign(marqueeRef.current.style, {
          display: "block",
          left: `${marquee.x}px`,
          top: `${marquee.y}px`,
          width: `${marquee.w}px`,
          height: `${marquee.h}px`,
        })
      }
    }
    const finish = (pointerEvent) => {
      document.removeEventListener("pointermove", move)
      document.removeEventListener("pointerup", finish)
      const current = { x: pointerEvent.clientX - rect.left, y: pointerEvent.clientY - rect.top }
      const ids = marqueeSelection(
        {
          x: Math.min(start.x, current.x),
          y: Math.min(start.y, current.y),
          w: Math.abs(current.x - start.x),
          h: Math.abs(current.y - start.y),
        },
        widgets,
      )
      if (marqueeRef.current) marqueeRef.current.style.display = "none"
      setSelectedWidgetIds((selected) =>
        event.shiftKey || event.ctrlKey || event.metaKey ? [...new Set([...selected, ...ids])] : ids,
      )
      setActiveWidgetId(ids.at(-1) || null)
    }
    document.addEventListener("pointermove", move)
    document.addEventListener("pointerup", finish)
  }
  const dropWidget = (event) => {
    event.preventDefault()
    const type = event.dataTransfer.getData("application/x-datox-widget") || draggingWidgetType
    if (!type) return
    const rect = pageRef.current?.getBoundingClientRect()
    if (!rect) return
    const canvasWidth = config.canvas?.width || 1123
    addWidget(type, {
      x: Math.max(0, Math.min(canvasWidth - 140, Math.round(event.clientX - rect.left - 120))),
      y: Math.max(0, Math.round(event.clientY - rect.top - 40)),
    })
    setDraggingWidgetType("")
  }

  const submitCreate = (event) => {
    event.preventDefault()
    const name = newName.trim()
    if (!name) return
    setCreateOpen(false)
    setNewName("")
    props.onCreateDashboard?.(name)
  }

  useEffect(() => {
    const keydown = (event) => {
      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
      )
        return
      const modifier = event.ctrlKey || event.metaKey
      if (modifier && event.key.toLowerCase() === "a") {
        event.preventDefault()
        const ids = widgets.map((widget) => widget.id)
        setSelectedWidgetIds(ids)
        setActiveWidgetId(ids.at(-1) || null)
        return
      }
      if (modifier && event.key.toLowerCase() === "c" && selectedWidgetIds.length) {
        event.preventDefault()
        clipboardRef.current = widgets
          .filter((widget) => selectedWidgetIds.includes(widget.id))
          .map(cloneDashboardConfig)
        return
      }
      if (modifier && event.key.toLowerCase() === "d" && selectedWidgetIds.length) {
        event.preventDefault()
        duplicateSelectedWidgets()
        return
      }
      if (modifier && event.key.toLowerCase() === "v" && clipboardRef.current.length) {
        event.preventDefault()
        const placed = []
        clipboardRef.current.forEach((source) => {
          const copy = { ...cloneDashboardConfig(source), id: widgetIdValue() }
          copy.layout = nearestFreePlacement(
            { ...copy.layout, x: copy.layout.x + 28, y: copy.layout.y + 28 },
            [...widgets, ...placed],
            config.canvas?.width || 1123,
            snapEnabled ? 4 : 1,
          )
          placed.push(copy)
        })
        updateWidgets([...widgets, ...placed])
        setSelectedWidgetIds(placed.map((widget) => widget.id))
        setActiveWidgetId(placed.at(-1)?.id || null)
        return
      }
      if (modifier && event.key.toLowerCase() === "z") {
        event.preventDefault()
        if (event.shiftKey) redo()
        else undo()
        return
      }
      if (modifier && event.key.toLowerCase() === "y") {
        event.preventDefault()
        redo()
        return
      }
      if ((event.key === "Delete" || event.key === "Backspace") && selectedWidgetIds.length) {
        event.preventDefault()
        updateWidgets(widgets.filter((widget) => !selectedWidgetIds.includes(widget.id)))
        setSelectedWidgetIds([])
        setActiveWidgetId(null)
        setEditorWidgetId(null)
        return
      }
      if (!selectedWidgetIds.length || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return
      event.preventDefault()
      const amount = event.shiftKey ? 10 : snapEnabled ? 4 : 1
      const requested = {
        x: event.key === "ArrowLeft" ? -amount : event.key === "ArrowRight" ? amount : 0,
        y: event.key === "ArrowUp" ? -amount : event.key === "ArrowDown" ? amount : 0,
      }
      const selected = widgets.filter((widget) => selectedWidgetIds.includes(widget.id))
      const delta = boundedGroupMove(selected, requested, {
        width: config.canvas?.width || 1123,
        height: config.canvas?.height || 794,
      })
      const moved = selected.map((widget) => ({
        ...widget.layout,
        x: widget.layout.x + delta.x,
        y: widget.layout.y + delta.y,
      }))
      if (moved.some((layout) => placementCollides(layout, widgets, selectedWidgetIds))) return
      const movedByID = new Map(selected.map((widget, index) => [widget.id, moved[index]]))
      updateWidgets(
        widgets.map((widget) => (movedByID.has(widget.id) ? { ...widget, layout: movedByID.get(widget.id) } : widget)),
      )
    }
    window.addEventListener("keydown", keydown)
    return () => window.removeEventListener("keydown", keydown)
  }, [config.canvas?.height, config.canvas?.width, selectedWidgetIds, snapEnabled, widgets])

  return (
    <div
      className={`datox-react-dashboard${exportMenuOpen || dashboardMenuOpen ? " datox-react-dashboard--menu-open" : ""}`}
      onClick={closeMenusOnOutsideClick}
    >
      <div className="datox-react-dashboard__topbar">
        <div className="datox-react-dashboard__topbar-left">
          <h2 className="datox-react-dashboard__title">{labels.dashboardTitle}</h2>
          <select
            className="datox-react-dashboard__select"
            value={props.selectedDashboardID || ""}
            onChange={(event) => props.onSelectDashboard?.(event.currentTarget.value)}
          >
            {(props.dashboards || []).map((dashboard) => (
              <option key={dashboard.id} value={dashboard.id}>
                {dashboard.name}
              </option>
            ))}
          </select>
          <DashboardCreateButton
            store={props.saveStatusStore}
            label={labels.createDashboard}
            onClick={() => setCreateOpen(true)}
          />
        </div>
        <div className="datox-react-dashboard__topbar-right">
          <div className="datox-react-dashboard__toolbar-group datox-react-dashboard__toolbar-group--plain">
            <button
              className="datox-react-dashboard__toolbar-button datox-react-dashboard__toolbar-button--text"
              type="button"
              title={labels.clients}
              onClick={() => setClientAccessOpen(true)}
            >
              {labels.clients}
            </button>
            <span
              className={
                selectedDashboard?.published_config || selectedDashboard?.status === "published"
                  ? "datox-react-dashboard__publish-status datox-react-dashboard__publish-status--published"
                  : "datox-react-dashboard__publish-status"
              }
            >
              {selectedDashboard?.published_config || selectedDashboard?.status === "published"
                ? labels.publishedStatus
                : labels.unpublishedStatus}
            </span>
            <button
              className="datox-react-dashboard__toolbar-button"
              type="button"
              title={labels.preview}
              onClick={() => setPreviewOpen(true)}
            >
              ▶
            </button>
          </div>
          <div className="datox-react-dashboard__toolbar-group">
            <button
              className={`datox-react-dashboard__toolbar-button ${config.canvas?.orientation === "portrait" ? "datox-react-dashboard__toolbar-button--active" : ""}`}
              type="button"
              title={labels.portrait}
              onClick={() => setOrientation("portrait")}
            >
              ▯
            </button>
            <button
              className={`datox-react-dashboard__toolbar-button ${config.canvas?.orientation !== "portrait" ? "datox-react-dashboard__toolbar-button--active" : ""}`}
              type="button"
              title={labels.landscape}
              onClick={() => setOrientation("landscape")}
            >
              ▭
            </button>
          </div>
          <div
            className="datox-react-dashboard__toolbar-group datox-react-dashboard__toolbar-group--continuation"
            aria-hidden={historyVersion < 0}
          >
            <button
              className="datox-react-dashboard__toolbar-button"
              type="button"
              title={labels.undo}
              onClick={undo}
              disabled={undoStackRef.current.length === 0}
            >
              ↶
            </button>
            <button
              className="datox-react-dashboard__toolbar-button"
              type="button"
              title={labels.redo}
              onClick={redo}
              disabled={redoStackRef.current.length === 0}
            >
              ↷
            </button>
          </div>
          <label className="datox-react-dashboard__drag-toggle">
            <span>{labels.drag}</span>
            <input
              type="checkbox"
              checked={dragEnabled}
              onChange={(event) => setDragEnabled(event.currentTarget.checked)}
            />
          </label>
          <label className="datox-react-dashboard__drag-toggle">
            <span>{labels.grid}</span>
            <input
              type="checkbox"
              checked={snapEnabled}
              onChange={(event) => setSnapEnabled(event.currentTarget.checked)}
            />
          </label>
          <div className="datox-react-dashboard__menu-wrap">
            <button
              className="datox-react-dashboard__toolbar-button"
              type="button"
              title={labels.exportMenu}
              aria-label={labels.exportMenu}
              onClick={() => setExportMenuOpen(!exportMenuOpen)}
            >
              <TbFileExport size={18} />
            </button>
            {exportMenuOpen ? (
              <div className="datox-react-dashboard__menu">
                <button
                  type="button"
                  disabled={exportingSnapshot}
                  onClick={() => {
                    setExportMenuOpen(false)
                    setSnapshotTabIds((config.tabs || []).map((tab) => tab.id))
                    setSnapshotFullTables(false)
                    setSnapshotOptionsOpen(true)
                  }}
                >
                  {labels.exportPdf}
                </button>
                <button
                  type="button"
                  disabled={!(selectedDashboard?.published_config || selectedDashboard?.status === "published")}
                  onClick={() => {
                    setExportMenuOpen(false)
                    setShareAccessOpen(true)
                  }}
                >
                  {labels.shareAccess}
                </button>
              </div>
            ) : null}
          </div>
          <div className="datox-react-dashboard__menu-wrap">
            <button
              className="datox-react-dashboard__toolbar-button"
              type="button"
              title={labels.more}
              onClick={() => setDashboardMenuOpen(!dashboardMenuOpen)}
            >
              ...
            </button>
            {dashboardMenuOpen ? (
              <div className="datox-react-dashboard__menu">
                <button
                  type="button"
                  onClick={() => {
                    setDashboardMenuOpen(false)
                    setPreviewOpen(true)
                  }}
                >
                  {labels.preview}
                </button>
                {selectedDashboard?.published_config || selectedDashboard?.status === "published" ? (
                  <button
                    type="button"
                    onClick={() => {
                      setDashboardMenuOpen(false)
                      props.onUnpublishDashboard?.()
                    }}
                  >
                    {labels.unpublishDashboard}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setDashboardMenuOpen(false)
                      publishDashboard()
                    }}
                  >
                    {labels.publishDashboard}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setDashboardMenuOpen(false)
                    openRenameDashboard()
                  }}
                >
                  {labels.renameDashboard}
                </button>
                <button
                  type="button"
                  className="datox-react-dashboard__menu-danger"
                  onClick={() => {
                    setDashboardMenuOpen(false)
                    setDeleteDashboardOpen(true)
                  }}
                >
                  {labels.deleteDashboard}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {props.error ? <div className="datox-react-dashboard__error">{props.error}</div> : null}
      {exportError ? (
        <div className="datox-react-dashboard__error" role="alert">
          {exportError}
        </div>
      ) : null}
      {/* {exportingPdf ? <div role="status">{labels.kpi.exportPreparing}</div> : null} */}
      <div className="datox-react-dashboard__tabs">
        {(config.tabs || []).map((tab) => (
          <div
            key={tab.id}
            className={`datox-react-dashboard__tab ${tab.id === activeTab.id ? "datox-react-dashboard__tab--active" : ""}`}
          >
            <button
              className="datox-react-dashboard__tab-name"
              type="button"
              onClick={() => {
                setActiveTabId(tab.id)
                setActiveWidgetId(null)
                setSelectedWidgetIds([])
              }}
              onDoubleClick={() => openRenameTab(tab)}
              title={labels.doubleClickRename}
            >
              {tab.name || labels.defaultTabName}
            </button>
            <button
              className="datox-react-dashboard__tab-menu-button"
              type="button"
              onClick={() => setTabMenuId(tabMenuId === tab.id ? null : tab.id)}
            >
              ⌄
            </button>
            {tabMenuId === tab.id ? (
              <div className="datox-react-dashboard__tab-menu">
                <button
                  type="button"
                  onClick={() => {
                    setTabMenuId(null)
                    openRenameTab(tab)
                  }}
                >
                  {labels.rename}
                </button>
                <button type="button" onClick={() => duplicateTab(tab)}>
                  {labels.duplicateTab}
                </button>
                <button
                  type="button"
                  className="datox-react-dashboard__tab-menu-danger"
                  disabled={(config.tabs || []).length <= 1}
                  onClick={() => deleteTab(tab.id)}
                >
                  {labels.deleteTab}
                </button>
              </div>
            ) : null}
          </div>
        ))}
        <button className="datox-react-dashboard__tab" type="button" onClick={addTab}>
          +
        </button>
      </div>
      <div
        className={`datox-react-dashboard__body ${!leftPanelOpen ? "datox-react-dashboard__body--left-collapsed" : ""} ${editorWidget ? "datox-react-dashboard__body--editor" : ""}`}
        style={{ "--datox-inspector-width": `${inspectorWidth}px` }}
      >
        {leftPanelOpen ? (
          <aside className="datox-react-dashboard__sidebar">
            <div className="datox-react-dashboard__panel-header">
              <h3>{labels.panel}</h3>
              <button
                className="datox-react-dashboard__panel-close"
                type="button"
                onClick={() => setLeftPanelOpen(false)}
                title={labels.closePanel}
              >
                ×
              </button>
            </div>
            <div className="datox-react-dashboard__segmented datox-react-dashboard__segmented--three">
              <button
                className={`datox-react-dashboard__segment ${panel === "widgets" ? "datox-react-dashboard__segment--active" : ""}`}
                type="button"
                onClick={() => setPanel("widgets")}
              >
                {labels.widgetsPanel}
              </button>
              <button
                className={`datox-react-dashboard__segment ${panel === "data" ? "datox-react-dashboard__segment--active" : ""}`}
                type="button"
                onClick={() => setPanel("data")}
              >
                {labels.data}
              </button>
              <button
                className={`datox-react-dashboard__segment ${panel === "design" ? "datox-react-dashboard__segment--active" : ""}`}
                type="button"
                onClick={() => setPanel("design")}
              >
                {labels.design}
              </button>
            </div>
            {panel === "widgets" ? (
              <>
                <div className="datox-react-dashboard__search-row">
                  <div className="datox-react-dashboard__search-wrap">
                    <span>/</span>
                    <input
                      className="datox-react-dashboard__input"
                      placeholder={labels.searchWidgets}
                      value={search}
                      onChange={(event) => setSearch(event.currentTarget.value)}
                    />
                  </div>
                  <div className="datox-react-dashboard__view-toggle">
                    <button
                      className={widgetLibraryView === "list" ? "datox-react-dashboard__view-toggle-active" : ""}
                      type="button"
                      onClick={() => setWidgetLibraryView("list")}
                      title={labels.listView}
                    >
                      =
                    </button>
                    <button
                      className={widgetLibraryView === "grid" ? "datox-react-dashboard__view-toggle-active" : ""}
                      type="button"
                      onClick={() => setWidgetLibraryView("grid")}
                      title={labels.gridView}
                    >
                      ::
                    </button>
                  </div>
                </div>
                <div className="datox-react-dashboard__section-label">{labels.widgetLibrary}</div>
                <div
                  className={
                    widgetLibraryView === "grid"
                      ? "datox-react-dashboard__palette datox-react-dashboard__palette--grid"
                      : "datox-react-dashboard__palette"
                  }
                >
                  {paletteItems.map((item) => {
                    const Icon = item.icon
                    return (
                      <button
                        key={item.type}
                        className="datox-react-dashboard__palette-item"
                        type="button"
                        draggable={!(item.data && !selectedTable)}
                        onDragStart={(event) => {
                          setDraggingWidgetType(item.type)
                          event.dataTransfer.effectAllowed = "copy"
                          event.dataTransfer.setData("application/x-datox-widget", item.type)
                        }}
                        onDragEnd={() => setDraggingWidgetType("")}
                        onClick={() => addWidget(item.type)}
                        disabled={item.data && !selectedTable}
                      >
                        <Icon className="datox-react-dashboard__palette-icon" />
                        {widgetLabel(labels, item.type)}
                      </button>
                    )
                  })}
                </div>
              </>
            ) : panel === "data" ? (
              <div className="datox-react-dashboard__data-panel">
                <label>
                  <div className="datox-react-dashboard__field-label">{labels.outputTable}</div>
                  <select
                    className="datox-react-dashboard__select"
                    style={{ width: "100%", minWidth: 0 }}
                    value={props.selectedDatasetID || ""}
                    onChange={(event) => props.onSelectDataset?.(event.currentTarget.value)}
                  >
                    {(props.datasets || []).map((dataset) => (
                      <option key={dataset.id} value={dataset.id}>
                        {dataset.name}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="datox-react-dashboard__metadata-card" aria-label={labels.tableMetadata}>
                  <div className="datox-react-dashboard__metadata-tile">
                    <span>{labels.rows}</span>
                    <strong>{selectedDataset?.row_count ?? selectedTable?.rows?.length ?? 0}</strong>
                  </div>
                  <div className="datox-react-dashboard__metadata-tile">
                    <span>{labels.columns}</span>
                    <strong>{selectedDataset?.columns?.length ?? selectedTable?.headers?.length ?? 0}</strong>
                  </div>
                </div>
                <div className="datox-react-dashboard__section-label">{labels.columnMetadata}</div>
                <div className="datox-react-dashboard__column-metadata-list">
                  {(
                    selectedDataset?.columns ||
                    selectedTable?.headers?.map((name) => ({ name, type: "string" })) ||
                    []
                  ).map((column) => (
                    <div key={column.name} className="datox-react-dashboard__column-metadata-row">
                      <span>{column.name}</span>
                      <small>{column.type}</small>
                    </div>
                  ))}
                </div>
                <div className="datox-react-dashboard__preview-section">
                  <div className="datox-react-dashboard__section-label">{labels.tablePreview}</div>
                  <div className="datox-react-dashboard__preview-wrap">
                    <TableWidget table={selectedTable} limit={8} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="datox-react-dashboard__design-panel">
                <div>
                  <div className="datox-react-dashboard__field-label">{labels.logo}</div>
                  <button className="datox-react-dashboard__upload-box" type="button">
                    {labels.uploadLogo}
                  </button>
                </div>
                <ColorSwatches
                  label={labels.accentColor}
                  value={config.design?.accent || "#2f80ed"}
                  onChange={(accent) => updateConfig({ ...config, design: { ...(config.design || {}), accent } })}
                  colors={[
                    "#2f80ed",
                    "#2f54eb",
                    "#7048e8",
                    "#40a9ff",
                    "#339989",
                    "#2f855a",
                    "#d6334c",
                    "#e8590c",
                    "#1d4ed8",
                    "#475467",
                  ]}
                />
                <ColorSwatches
                  label={labels.headerColor}
                  value={config.design?.headerColor || "#101828"}
                  onChange={(headerColor) =>
                    updateConfig({ ...config, design: { ...(config.design || {}), headerColor } })
                  }
                  colors={["#101828", "#1f2937", "#1e3a8a", "#1d4ed8", "#475467", "#ffffff", "#111827"]}
                />
                <SegmentedControl
                  label={labels.cornerRadius}
                  value={config.design?.radius || "medium"}
                  options={[
                    { value: "square", label: labels.radiusSquare },
                    { value: "medium", label: labels.radiusMedium },
                    { value: "large", label: labels.radiusLarge },
                  ]}
                  onChange={(radius) => updateConfig({ ...config, design: { ...(config.design || {}), radius } })}
                />
                <SegmentedControl
                  label={labels.fontSize}
                  value={config.design?.fontSize || "normal"}
                  options={[
                    { value: "compact", label: labels.fontCompact },
                    { value: "normal", label: labels.fontNormal },
                    { value: "large", label: labels.fontLarge },
                  ]}
                  onChange={(fontSize) => updateConfig({ ...config, design: { ...(config.design || {}), fontSize } })}
                />
                <label>
                  <div className="datox-react-dashboard__field-label">{labels.headingFont}</div>
                  <select
                    className="datox-react-dashboard__select"
                    style={{ width: "100%", minWidth: 0 }}
                    value={config.design?.headingFont || "System"}
                    onChange={(event) =>
                      updateConfig({
                        ...config,
                        design: { ...(config.design || {}), headingFont: event.currentTarget.value },
                      })
                    }
                  >
                    <option value="System">System</option>
                    <option value="Inter">Inter</option>
                    <option value="Serif">Serif</option>
                  </select>
                </label>
                <label>
                  <div className="datox-react-dashboard__field-label">{labels.bodyFont}</div>
                  <select
                    className="datox-react-dashboard__select"
                    style={{ width: "100%", minWidth: 0 }}
                    value={config.design?.bodyFont || "System"}
                    onChange={(event) =>
                      updateConfig({
                        ...config,
                        design: { ...(config.design || {}), bodyFont: event.currentTarget.value },
                      })
                    }
                  >
                    <option value="System">System</option>
                    <option value="Inter">Inter</option>
                    <option value="Serif">Serif</option>
                  </select>
                </label>
                <ToggleRow
                  label={labels.widgetShadows}
                  checked={Boolean(config.design?.widgetShadows)}
                  onChange={(widgetShadows) =>
                    updateConfig({ ...config, design: { ...(config.design || {}), widgetShadows } })
                  }
                />
                <ToggleRow
                  label={labels.lightGrayBackground}
                  checked={Boolean(config.design?.lightGrayBackground)}
                  onChange={(lightGrayBackground) =>
                    updateConfig({ ...config, design: { ...(config.design || {}), lightGrayBackground } })
                  }
                />
              </div>
            )}
          </aside>
        ) : null}
        <main
          className={`datox-react-dashboard__workspace ${draggingWidgetType ? "datox-react-dashboard__workspace--dragging" : ""}`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={dropWidget}
        >
          {!leftPanelOpen ? (
            <button
              className="datox-react-dashboard__panel-open"
              type="button"
              onClick={() => setLeftPanelOpen(true)}
              title={labels.openPanel}
            >
              ☰
            </button>
          ) : null}
          <div
            className="datox-react-dashboard__page-wrap"
            style={{ width: (config.canvas?.width || 1123) + 56, minWidth: (config.canvas?.width || 1123) + 56 }}
          >
            <div
              ref={pageRef}
              className={`datox-react-dashboard__page ${config.design?.lightGrayBackground ? "datox-react-dashboard__page--gray" : ""} ${draggingWidgetType ? "datox-react-dashboard__page--drop-target" : ""}`}
              style={{
                "--ds-accent": config.design?.accent || "#2f80ed",
                "--ds-header-color": config.design?.headerColor || "#101828",
                "--ds-heading-font": designFont(config.design?.headingFont),
                "--ds-body-font": designFont(config.design?.bodyFont),
                width: config.canvas?.width || 1123,
                minHeight: dashboardKpiPageHeight(config.canvas?.height, widgets),
              }}
              onPointerDown={startMarquee}
            >
              <div ref={marqueeRef} className="datox-react-dashboard__marquee" />
              <div
                ref={verticalGuideRef}
                className="datox-react-dashboard__guide datox-react-dashboard__guide--vertical"
              />
              <div
                ref={horizontalGuideRef}
                className="datox-react-dashboard__guide datox-react-dashboard__guide--horizontal"
              />
              {selectedWidgetIds.length ? (
                <div className="datox-react-dashboard__selection-toolbar">
                  <span>{labels.widgetCount.replace("{count}", String(selectedWidgetIds.length))}</span>
                  <button type="button" title={labels.duplicateWidget} onClick={duplicateSelectedWidgets}>
                    {labels.duplicateWidget}
                  </button>
                  <button type="button" title={labels.deleteWidget} onClick={deleteSelectedWidgets}>
                    {labels.deleteWidget}
                  </button>
                </div>
              ) : null}
              {widgets.length === 0 ? (
                <div className="datox-react-dashboard__empty">
                  <div>
                    <strong>{labels.emptyCanvasTitle}</strong>
                    <br />
                    {labels.emptyCanvasDescription}
                  </div>
                </div>
              ) : null}
              {widgets.map((widget) => (
                <Rnd
                  key={widget.id}
                  ref={(instance) => {
                    if (instance) widgetRefs.current.set(widget.id, instance)
                    else widgetRefs.current.delete(widget.id)
                  }}
                  bounds="parent"
                  dragGrid={[snapEnabled ? 4 : 1, snapEnabled ? 4 : 1]}
                  resizeGrid={[snapEnabled ? 4 : 1, snapEnabled ? 4 : 1]}
                  disableDragging={!dragEnabled}
                  dragHandleClassName="datox-react-dashboard__drag-handle"
                  cancel='.datox-react-dashboard__widget-menu, .datox-react-dashboard__widget-dropdown, .datox-react-dashboard__chart-wrap, .datox-react-dashboard__table-container, input, textarea, select, button, a, table, [contenteditable="true"]'
                  minWidth={120}
                  minHeight={widget.type === "kpi" && widget.style?.target ? 180 : 56}
                  position={{ x: widget.layout.x, y: widget.layout.y }}
                  size={{ width: widget.layout.w, height: dashboardWidgetHeight(widget) }}
                  onDragStart={() => {
                    const ids = selectedWidgetIds.includes(widget.id) ? selectedWidgetIds : [widget.id]
                    const selected = widgets.filter((item) => ids.includes(item.id))
                    dragStateRef.current = {
                      activeId: widget.id,
                      ids,
                      widgets: selected,
                      delta: { x: 0, y: 0 },
                      invalid: false,
                    }
                  }}
                  onDrag={(event, data) => previewGroupDrag(widget, data)}
                  onDragStop={finishGroupDrag}
                  onResize={(event, direction, ref, delta, position) => {
                    const layout = {
                      x: position.x,
                      y: position.y,
                      w: ref.offsetWidth,
                      h: ref.offsetHeight,
                    }
                    resizeInvalidRef.current = placementCollides(layout, widgets, [widget.id])
                    setCollisionPreview([widget.id], resizeInvalidRef.current)
                  }}
                  onResizeStop={(event, direction, ref, delta, position) => {
                    setCollisionPreview([widget.id], false)
                    if (resizeInvalidRef.current) {
                      widgetRefs.current.get(widget.id)?.updatePosition(widget.layout)
                      widgetRefs.current.get(widget.id)?.updateSize({
                        width: widget.layout.w,
                        height: dashboardWidgetHeight(widget),
                      })
                      resizeInvalidRef.current = false
                      return
                    }
                    const layout = snapLayout(
                      { x: position.x, y: position.y, w: ref.offsetWidth, h: ref.offsetHeight },
                      snapEnabled ? 4 : 1,
                    )
                    updateWidget(widget.id, { layout })
                  }}
                  onMouseDown={(event) => selectWidget(widget.id, event)}
                  style={{ zIndex: selectedWidgetIds.includes(widget.id) ? 5 : 1 }}
                >
                  <WidgetFrame
                    labels={labels}
                    filterWidgets={activeFilterWidgets}
                    widget={widget}
                    draggable={dragEnabled}
                    table={applyDashboardFilters(
                      props.outputTables.find((table) => table.datasetID === widget.data?.dataset_id),
                      widget,
                      activeFilterWidgets,
                    )}
                    accent={config.design?.accent || "#2f80ed"}
                    design={config.design || {}}
                    comparison={comparisonState.itemsByWidgetId[widget.id]}
                    selected={selectedWidgetIds.includes(widget.id)}
                    menuOpen={widgetMenuId === widget.id}
                    inlineEditing={inlineEditWidgetId === widget.id}
                    onInlineEdit={() => setInlineEditWidgetId(widget.id)}
                    onInlineEditClose={() => setInlineEditWidgetId(null)}
                    onInlineTextChange={(value) =>
                      updateWidget(widget.id, widget.type === "title" ? { title: value } : { text: value })
                    }
                    onToggleMenu={() => setWidgetMenuId(widgetMenuId === widget.id ? null : widget.id)}
                    onRename={() => {
                      setWidgetMenuId(null)
                      openRenameWidget(widget)
                    }}
                    onEditData={() => openWidgetEditor(widget, "data")}
                    onEditDesign={() => openWidgetEditor(widget, "design")}
                    onExplainSource={
                      props.onExplainSource && widget.data?.dataset_id
                        ? () => {
                            const request = {
                              dashboardID: props.selectedDashboardID,
                              dashboardName: config.title || labels.dashboardTitle,
                              tabID: activeTab.id,
                              tabName: activeTab.name,
                              widgetID: widget.id,
                              widgetType: widget.type,
                              widgetTitle: widget.title,
                              widgetData: widget.data || {},
                            }
                            setWidgetMenuId(null)
                            void loadSourceExplanation(request)
                          }
                        : undefined
                    }
                    onExportTable={() => {
                      setWidgetMenuId(null)
                      exportWidgetTable(widget)
                    }}
                    onFullView={() => {
                      setWidgetMenuId(null)
                      setFullViewWidgetId(widget.id)
                    }}
                    onFilterValueToggle={(value) => toggleFilterValue(widget, value)}
                    onDelete={() => {
                      setWidgetMenuId(null)
                      deleteWidget(widget.id)
                    }}
                    onDuplicate={() => {
                      setWidgetMenuId(null)
                      duplicateWidget(widget.id)
                    }}
                  />
                </Rnd>
              ))}
            </div>
          </div>
        </main>
        {editorWidget ? (
          <DashboardInspectorResize
            width={inspectorWidth}
            onChange={setInspectorWidth}
            leftPanelOpen={leftPanelOpen}
            labels={labels.kpi}
          />
        ) : null}
        {editorWidget ? (
          <aside className="datox-react-dashboard__editor-panel">
            <div className="datox-react-dashboard__editor-sticky">
              <div className="datox-react-dashboard__editor-header">
                <h3>{labels.editWidget}</h3>
                <button type="button" aria-label={labels.closePanel} onClick={() => setEditorWidgetId(null)}>
                  ×
                </button>
              </div>
              <div className="datox-react-dashboard__segmented">
                <button
                  className={`datox-react-dashboard__segment ${widgetEditorPanel === "data" ? "datox-react-dashboard__segment--active" : ""}`}
                  type="button"
                  onClick={() => setWidgetEditorPanel("data")}
                >
                  {labels.data}
                </button>
                <button
                  className={`datox-react-dashboard__segment ${widgetEditorPanel === "design" ? "datox-react-dashboard__segment--active" : ""}`}
                  type="button"
                  onClick={() => setWidgetEditorPanel("design")}
                >
                  {labels.design}
                </button>
              </div>
            </div>
            <div className="datox-react-dashboard__editor-scroll">
              <label>
                <div className="datox-react-dashboard__field-label">{labels.widgetType}</div>
                <select
                  className="datox-react-dashboard__select"
                  style={{ width: "100%", minWidth: 0 }}
                  value={editorWidget.type}
                  onChange={(event) => changeWidgetType(event.currentTarget.value)}
                >
                  {WIDGETS.map((widget) => (
                    <option key={widget.type} value={widget.type}>
                      {widgetLabel(labels, widget.type)}
                    </option>
                  ))}
                </select>
              </label>
              {widgetEditorPanel === "data" ? (
                <div className="datox-react-dashboard__editor-section">
                  <div className="datox-react-dashboard__field-label">{labels.data}</div>
                  {editorWidget.type === "text" || editorWidget.type === "title" ? (
                    <label>
                      <span>{editorWidget.type === "title" ? labels.titleContent : labels.textContent}</span>
                      <textarea
                        className="datox-react-dashboard__textarea"
                        value={editorWidget.type === "title" ? editorWidget.title || "" : editorWidget.text || ""}
                        placeholder={
                          editorWidget.type === "title" ? widgetLabel(labels, "title") : labels.textPlaceholder
                        }
                        onChange={(event) =>
                          updateWidget(
                            editorWidget.id,
                            editorWidget.type === "title"
                              ? { title: event.currentTarget.value }
                              : { text: event.currentTarget.value },
                          )
                        }
                      />
                    </label>
                  ) : editorWidget.type === "freeTable" && editorWidget.data?.freeStyle ? null : (
                    <label>
                      <span>{labels.widgetDataset}</span>
                      <select
                        className="datox-react-dashboard__select"
                        value={editorWidget.data?.dataset_id || ""}
                        onChange={(event) => updateEditorWidgetData({ dataset_id: event.currentTarget.value })}
                      >
                        {(props.datasets || []).map((dataset) => (
                          <option key={dataset.id} value={dataset.id}>
                            {dataset.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {editorWidget.type !== "text" && editorWidget.type !== "title" ? (
                    <>
                      {editorWidget.type === "filter" ? (
                        <div className="datox-react-dashboard__filter-targets">
                          <div className="datox-react-dashboard__field-label">{labels.filterTargets}</div>
                          {(props.datasets || []).map((dataset) => {
                            const target = (editorWidget.data?.targets || []).find(
                              (item) => item.dataset_id === dataset.id,
                            )
                            const columns = datasetColumns(dataset.id, props.outputTables, props.datasets)
                            return (
                              <div key={dataset.id} className="datox-react-dashboard__filter-target-row">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(target)}
                                    onChange={(event) =>
                                      event.currentTarget.checked
                                        ? updateFilterTarget(dataset.id, {})
                                        : removeFilterTarget(dataset.id)
                                    }
                                  />{" "}
                                  <span>{dataset.name}</span>
                                </label>
                                <select
                                  disabled={!target}
                                  value={target?.column || columns[0] || ""}
                                  onChange={(event) =>
                                    updateFilterTarget(dataset.id, { column: event.currentTarget.value })
                                  }
                                >
                                  {columns.map((column) => (
                                    <option key={column} value={column}>
                                      {column}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )
                          })}
                        </div>
                      ) : null}
                      {editorWidget.type === "freeTable" && editorWidget.data?.freeStyle ? (
                        <FreeStyleEditor
                          key={editorWidget.id}
                          value={editorWidget.data.freeStyle}
                          labels={labels}
                          onChange={(freeStyle) => updateEditorWidgetData({ freeStyle })}
                        />
                      ) : null}
                      {editorWidget.type === "freeTable" && !editorWidget.data?.freeStyle ? (
                        <>
                          <p className="datox-react-dashboard__settings-help">{labels.conversionHelp}</p>
                          <button
                            type="button"
                            className="datox-react-dashboard__button"
                            disabled={
                              !editorWidgetConversionTable ||
                              editorWidgetConversionTable.loadError ||
                              editorWidgetConversionTable.headers.length > 20 ||
                              editorWidgetConversionTable.rows.length > 50
                            }
                            onClick={() =>
                              updateEditorWidgetData({
                                freeStyle: freeStyleFromTable(editorWidgetConversionTable, labels),
                                dataset_id: undefined,
                              })
                            }
                          >
                            {labels.editFreeStyle}
                          </button>
                          {editorWidgetConversionTable &&
                          (editorWidgetConversionTable.headers.length > 20 ||
                            editorWidgetConversionTable.rows.length > 50) ? (
                            <div role="status">{labels.freeStyleLimit}</div>
                          ) : null}
                          {editorWidgetConversionTable?.loadError ? (
                            <div role="alert">{labels.datasetLoadError}</div>
                          ) : null}
                        </>
                      ) : null}
                      {isChartWidget(editorWidget.type) ? (
                        <div className="datox-react-dashboard__chart-data-editor">
                          <div className="datox-react-dashboard__field-label">{labels.chartData}</div>
                          <div className="datox-react-dashboard__muted-text">
                            {chartDataSummary(editorWidget, labels)}
                          </div>
                          {editorWidget.type === "scatter" ? (
                            <>
                              {[
                                ["xColumn", labels.scatterX],
                                ["yColumn", labels.scatterY],
                                ["sizeColumn", labels.scatterSize],
                              ].map(([key, label]) => (
                                <label key={key}>
                                  <span>{label}</span>
                                  <select
                                    className="datox-react-dashboard__select"
                                    aria-invalid={Boolean(
                                      editorWidgetTable &&
                                      !editorWidgetTable.loadError &&
                                      (((key !== "sizeColumn" || editorWidget.data?.[key]) &&
                                        !editorWidgetNumericColumns.includes(editorWidget.data?.[key])) ||
                                        (key === "yColumn" &&
                                          editorWidget.data?.xColumn &&
                                          editorWidget.data.xColumn === editorWidget.data?.yColumn)),
                                    )}
                                    value={editorWidget.data?.[key] || ""}
                                    onChange={(event) => updateEditorWidgetData({ [key]: event.currentTarget.value })}
                                  >
                                    <option value="">
                                      {key === "sizeColumn" ? labels.scatterSizeNone : labels.noBinding}
                                    </option>
                                    {editorWidget.data?.[key] &&
                                    !editorWidgetNumericColumns.includes(editorWidget.data[key]) ? (
                                      <option value={editorWidget.data[key]} disabled>
                                        {editorWidget.data[key]}
                                      </option>
                                    ) : null}
                                    {editorWidgetNumericColumns.map((column) => (
                                      <option key={column} value={column}>
                                        {column}
                                      </option>
                                    ))}
                                  </select>
                                  {editorWidgetTable &&
                                  !editorWidgetTable.loadError &&
                                  (key !== "sizeColumn" || editorWidget.data?.[key]) &&
                                  !editorWidgetNumericColumns.includes(editorWidget.data?.[key]) ? (
                                    <small className="datox-react-dashboard__field-error">
                                      {labels.numericBindingRequired}
                                    </small>
                                  ) : null}
                                  {key === "yColumn" &&
                                  editorWidget.data?.xColumn &&
                                  editorWidget.data.xColumn === editorWidget.data?.yColumn ? (
                                    <small className="datox-react-dashboard__field-error">
                                      {labels.scatterSameAxes}
                                    </small>
                                  ) : null}
                                </label>
                              ))}
                            </>
                          ) : (
                            <label>
                              <span>{labels.groupBy}</span>
                              <small>{labels.groupByHelp}</small>
                              <select
                                className="datox-react-dashboard__select"
                                value={editorWidget.data?.label || ""}
                                onChange={(event) => updateEditorWidgetData({ label: event.currentTarget.value })}
                              >
                                <option value="" disabled>
                                  {labels.noBinding}
                                </option>
                                {editorWidget.data?.label && !editorWidgetColumns.includes(editorWidget.data.label) ? (
                                  <option value={editorWidget.data.label} disabled>
                                    {editorWidget.data.label}
                                  </option>
                                ) : null}
                                {editorWidgetColumns.map((column) => (
                                  <option key={column} value={column}>
                                    {column}
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                          {editorWidget.type !== "scatter" ? (
                            <label>
                              <span>{labels.aggregation}</span>
                              <select
                                className="datox-react-dashboard__select"
                                value={editorWidget.data?.aggregation || "sum"}
                                onChange={(event) => updateEditorWidgetData({ aggregation: event.currentTarget.value })}
                              >
                                {["sum", "avg", "min", "max", "count"].map((operation) => (
                                  <option key={operation} value={operation}>
                                    {
                                      labels[
                                        `aggregation${operation === "avg" ? "Average" : operation[0].toUpperCase() + operation.slice(1)}`
                                      ]
                                    }
                                  </option>
                                ))}
                              </select>
                            </label>
                          ) : null}
                          {["pie", "treemap", "funnel", "heatmap"].includes(editorWidget.type) ? (
                            <label>
                              <span>{labels.metricColumn}</span>
                              <select
                                className="datox-react-dashboard__select"
                                value={editorWidget.data?.metric || ""}
                                onChange={(event) => updateEditorWidgetData({ metric: event.currentTarget.value })}
                              >
                                <option value="" disabled>
                                  {labels.noBinding}
                                </option>
                                {editorWidget.data?.metric &&
                                !editorWidgetNumericColumns.includes(editorWidget.data.metric) ? (
                                  <option value={editorWidget.data.metric} disabled>
                                    {editorWidget.data.metric}
                                  </option>
                                ) : null}
                                {editorWidgetNumericColumns.map((column) => (
                                  <option key={column} value={column}>
                                    {column}
                                  </option>
                                ))}
                              </select>
                            </label>
                          ) : null}
                          {["bar", "line", "multiSeries"].includes(editorWidget.type) ? (
                            <label>
                              <span>{labels.dataStreams}</span>
                              <small>{labels.dataStreamsHelp}</small>
                              <div className="datox-react-dashboard__visible-columns">
                                {editorWidgetNumericColumns.map((column) => (
                                  <button
                                    key={column}
                                    type="button"
                                    aria-pressed={(editorWidget.data?.measures || [editorWidget.data?.metric]).includes(
                                      column,
                                    )}
                                    className={
                                      (
                                        editorWidget.data?.measures || [editorWidget.data?.metric].filter(Boolean)
                                      ).includes(column)
                                        ? "datox-react-dashboard__column-chip datox-react-dashboard__column-chip--active"
                                        : "datox-react-dashboard__column-chip"
                                    }
                                    onClick={() => toggleMeasure(column)}
                                  >
                                    {column}
                                  </button>
                                ))}
                              </div>
                            </label>
                          ) : null}
                          {["bar", "line", "multiSeries"].includes(editorWidget.type) ? (
                            <EditorSection title={labels.dataStreamDisplayNames}>
                              {(editorWidget.data?.measures?.length
                                ? editorWidget.data.measures
                                : [editorWidget.data?.metric].filter(Boolean)
                              )
                                .filter(() => ["bar", "line", "multiSeries"].includes(editorWidget.type))
                                .map((measure) => (
                                  <label key={measure}>
                                    <span>{measure}</span>
                                    <input
                                      className="datox-react-dashboard__input"
                                      value={editorWidget.data?.displayNames?.[measure] || ""}
                                      placeholder={measure}
                                      onChange={(event) =>
                                        updateEditorWidgetData({
                                          displayNames: {
                                            ...(editorWidget.data?.displayNames || {}),
                                            [measure]: event.currentTarget.value,
                                          },
                                        })
                                      }
                                    />
                                  </label>
                                ))}
                            </EditorSection>
                          ) : null}
                          {editorWidget.type !== "scatter" ? (
                            <EditorSection title={labels.resultLimit}>
                              <p className="datox-react-dashboard__settings-help">{labels.resultLimitHelp}</p>
                              <div className="datox-react-dashboard__top-row">
                                <label>
                                  <span>{labels.showTop}</span>
                                  <input
                                    className="datox-react-dashboard__input"
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={editorWidget.data?.showTop || 12}
                                    onChange={(event) =>
                                      updateEditorWidgetData({ showTop: Number(event.currentTarget.value) || 12 })
                                    }
                                  />
                                </label>
                                <label>
                                  <span>{labels.by}</span>
                                  <select
                                    className="datox-react-dashboard__select"
                                    value={editorWidget.data?.topBy || ""}
                                    onChange={(event) => updateEditorWidgetData({ topBy: event.currentTarget.value })}
                                  >
                                    <option value="">{labels.noSort}</option>
                                    {(editorWidgetNumericColumns.length
                                      ? editorWidgetNumericColumns
                                      : editorWidgetColumns
                                    ).map((column) => (
                                      <option key={column} value={column}>
                                        {column}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label>
                                  <span>{labels.order}</span>
                                  <select
                                    className="datox-react-dashboard__select"
                                    disabled={!editorWidget.data?.topBy}
                                    value={editorWidget.data?.topOrder || "desc"}
                                    onChange={(event) =>
                                      updateEditorWidgetData({ topOrder: event.currentTarget.value })
                                    }
                                  >
                                    <option value="desc">{labels.sortDescending}</option>
                                    <option value="asc">{labels.sortAscending}</option>
                                  </select>
                                </label>
                              </div>
                            </EditorSection>
                          ) : null}
                        </div>
                      ) : null}
                      {editorWidget.type === "kpi" ? (
                        <>
                          <KpiFormulaSettings
                            widget={editorWidget}
                            columns={editorWidgetColumns}
                            labels={labels}
                            filterWidgets={activeFilterWidgets}
                            onChange={(value) => updateWidget(editorWidget.id, { kpi_calculation: value })}
                          >
                            <div className="datox-react-dashboard__chart-data-editor">
                              <div className="datox-react-dashboard__field-label">{labels.summarize}</div>
                              <label>
                                <span>{labels.metricColumn}</span>
                                <select
                                  className="datox-react-dashboard__select"
                                  value={editorWidget.data?.metric || ""}
                                  onChange={(event) => updateEditorWidgetData({ metric: event.currentTarget.value })}
                                >
                                  {(editorWidgetNumericColumns.length
                                    ? editorWidgetNumericColumns
                                    : editorWidgetColumns
                                  ).map((column) => (
                                    <option key={column} value={column}>
                                      {column}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                <span>{labels.aggregation}</span>
                                <select
                                  className="datox-react-dashboard__select"
                                  value={editorWidget.data?.aggregation || "sum"}
                                  onChange={(event) =>
                                    updateEditorWidgetData({ aggregation: event.currentTarget.value })
                                  }
                                >
                                  <option value="sum">{labels.aggregationSum}</option>
                                  <option value="avg">{labels.aggregationAverage}</option>
                                  <option value="min">{labels.aggregationMin}</option>
                                  <option value="max">{labels.aggregationMax}</option>
                                  <option value="count">{labels.aggregationCount}</option>
                                  <option value="first">{labels.aggregationFirst}</option>
                                  <option value="last">{labels.aggregationLast}</option>
                                </select>
                              </label>
                            </div>
                          </KpiFormulaSettings>
                          <KpiComparisonSettings
                            labels={labels}
                            widget={editorWidget}
                            options={comparisonState.options}
                            isStale={comparisonState.stale}
                            stateMessage={comparisonState.stale ? comparisonState.staleMessage : comparisonState.error}
                            onChange={(next) => updateWidget(editorWidget.id, { kpi_comparison: next })}
                            onReload={() => window.location.reload()}
                          />
                        </>
                      ) : null}
                      {editorWidget.type !== "filter" &&
                      !(editorWidget.type === "freeTable" && editorWidget.data?.freeStyle) ? (
                        <>
                          <details className="datox-react-dashboard__editor-details">
                            <summary>{labels.sampleData}</summary>
                            <div className="datox-react-dashboard__sample-scroll">
                              <TableWidget table={editorWidgetTable} limit={4} />
                            </div>
                          </details>
                          <EditorSection
                            title={labels.filtersAndSorting}
                            badge={editorWidget.data?.filters?.length || undefined}
                          >
                            <div className="datox-react-dashboard__field-label">{labels.filter}</div>
                            <button
                              className="datox-react-dashboard__calculation-button"
                              type="button"
                              onClick={addEditorFilter}
                            >
                              + {labels.addFilter}
                            </button>
                            {(editorWidget.data?.filters || []).length ? (
                              <div className="datox-react-dashboard__filter-editor-list">
                                {(editorWidget.data?.filters || []).map((filter) => (
                                  <div key={filter.id} className="datox-react-dashboard__filter-editor-row">
                                    <select
                                      value={filter.column || ""}
                                      onChange={(event) =>
                                        updateEditorFilter(filter.id, { column: event.currentTarget.value })
                                      }
                                    >
                                      {editorWidgetColumns.map((column) => (
                                        <option key={column} value={column}>
                                          {column}
                                        </option>
                                      ))}
                                    </select>
                                    <select
                                      value={filter.operator || "contains"}
                                      onChange={(event) =>
                                        updateEditorFilter(filter.id, { operator: event.currentTarget.value })
                                      }
                                    >
                                      <option value="contains">{labels.filterContains}</option>
                                      <option value="equals">{labels.filterEquals}</option>
                                      <option value="notEmpty">{labels.filterNotEmpty}</option>
                                    </select>
                                    <input
                                      value={filter.value || ""}
                                      placeholder={labels.valueLabel}
                                      onChange={(event) =>
                                        updateEditorFilter(filter.id, { value: event.currentTarget.value })
                                      }
                                    />
                                    <button type="button" onClick={() => removeEditorFilter(filter.id)}>
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="datox-react-dashboard__muted-text">{labels.noFiltersApplied}</div>
                            )}
                            <div className="datox-react-dashboard__field-label">{labels.sort}</div>
                            <div className="datox-react-dashboard__sort-row">
                              <select
                                value={editorWidget.data?.sort?.column || ""}
                                onChange={(event) =>
                                  updateEditorWidgetData({
                                    sort: { ...(editorWidget.data?.sort || {}), column: event.currentTarget.value },
                                  })
                                }
                              >
                                <option value="">{labels.noSort}</option>
                                {editorWidgetColumns.map((column) => (
                                  <option key={column} value={column}>
                                    {column}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={editorWidget.data?.sort?.direction || "asc"}
                                onChange={(event) =>
                                  updateEditorWidgetData({
                                    sort: { ...(editorWidget.data?.sort || {}), direction: event.currentTarget.value },
                                  })
                                }
                              >
                                <option value="asc">{labels.sortAscending}</option>
                                <option value="desc">{labels.sortDescending}</option>
                              </select>
                            </div>
                            <label>
                              <span>{labels.rowLimit}</span>
                              <input
                                className="datox-react-dashboard__input"
                                type="number"
                                min="1"
                                max="10000"
                                value={editorWidget.data?.rowLimit || ""}
                                placeholder={labels.noRowLimit}
                                onChange={(event) =>
                                  updateEditorWidgetData({
                                    rowLimit: event.currentTarget.value ? Number(event.currentTarget.value) : undefined,
                                  })
                                }
                              />
                            </label>
                          </EditorSection>
                          {["table", "freeTable"].includes(editorWidget.type) ? (
                            <>
                              <div className="datox-react-dashboard__field-label">{labels.columns}</div>
                              <div className="datox-react-dashboard__visible-columns">
                                {editorWidgetColumns.map((column) => (
                                  <button
                                    key={column}
                                    type="button"
                                    className={
                                      (editorWidget.data?.columns?.length
                                        ? editorWidget.data.columns
                                        : editorWidgetColumns
                                      ).includes(column)
                                        ? "datox-react-dashboard__column-chip datox-react-dashboard__column-chip--active"
                                        : "datox-react-dashboard__column-chip"
                                    }
                                    onClick={() => toggleEditorColumn(column)}
                                  >
                                    {column}
                                  </button>
                                ))}
                              </div>
                            </>
                          ) : null}
                        </>
                      ) : null}
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="datox-react-dashboard__editor-section">
                  <label>
                    <span>{labels.selectedWidgetTitle}</span>
                    <input
                      className="datox-react-dashboard__input"
                      value={editorWidget.title || ""}
                      onChange={(event) => updateWidget(editorWidget.id, { title: event.currentTarget.value })}
                    />
                  </label>
                  <SegmentedControl
                    label={labels.cornerRadius}
                    value={editorWidget.style?.radius || config.design?.radius || "medium"}
                    options={[
                      { value: "square", label: labels.radiusSquare },
                      { value: "medium", label: labels.radiusMedium },
                      { value: "large", label: labels.radiusLarge },
                    ]}
                    onChange={(radius) =>
                      updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), radius } })
                    }
                  />
                  <ColorSwatches
                    label={labels.accentColor}
                    value={editorWidget.style?.accent || config.design?.accent || "#2f80ed"}
                    onChange={(accent) =>
                      updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), accent } })
                    }
                    colors={["#2f80ed", "#2f54eb", "#7048e8", "#40a9ff", "#339989", "#2f855a", "#d6334c"]}
                  />
                  {editorWidget.type === "text" || editorWidget.type === "title" ? (
                    <div className="datox-react-dashboard__chart-design-editor">
                      <SegmentedControl
                        label={labels.textAlign}
                        value={editorWidget.style?.textAlign || "left"}
                        options={[
                          { value: "left", label: labels.alignLeft },
                          { value: "center", label: labels.alignCenter },
                          { value: "right", label: labels.alignRight },
                        ]}
                        onChange={(textAlign) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), textAlign } })
                        }
                      />
                      <SegmentedControl
                        label={labels.verticalAlign}
                        value={editorWidget.style?.verticalAlign || (editorWidget.type === "title" ? "center" : "top")}
                        options={[
                          { value: "top", label: labels.alignTop },
                          { value: "center", label: labels.alignMiddle },
                          { value: "bottom", label: labels.alignBottom },
                        ]}
                        onChange={(verticalAlign) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), verticalAlign } })
                        }
                      />
                      <label>
                        <span>{labels.fontSize}</span>
                        <input
                          className="datox-react-dashboard__input"
                          type="number"
                          min="8"
                          max="72"
                          value={editorWidget.style?.fontSize || (editorWidget.type === "title" ? 28 : 16)}
                          onChange={(event) =>
                            updateWidget(editorWidget.id, {
                              style: { ...(editorWidget.style || {}), fontSize: Number(event.currentTarget.value) },
                            })
                          }
                        />
                      </label>
                      <label>
                        <span>{labels.fontWeight}</span>
                        <select
                          className="datox-react-dashboard__select"
                          value={editorWidget.style?.fontWeight || (editorWidget.type === "title" ? "800" : "400")}
                          onChange={(event) =>
                            updateWidget(editorWidget.id, {
                              style: { ...(editorWidget.style || {}), fontWeight: event.currentTarget.value },
                            })
                          }
                        >
                          <option value="400">400</option>
                          <option value="500">500</option>
                          <option value="700">700</option>
                          <option value="800">800</option>
                          <option value="900">900</option>
                        </select>
                      </label>
                      <ColorSwatches
                        label={labels.textColor}
                        value={editorWidget.style?.textColor || "#101828"}
                        onChange={(textColor) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), textColor } })
                        }
                        colors={["#101828", "#344054", "#667085", "#2f80ed", "#2f54eb", "#d6334c", "#ffffff"]}
                      />
                    </div>
                  ) : null}
                  {editorWidget.type === "kpi" ? (
                    <div className="datox-react-dashboard__chart-design-editor">
                      <KpiDisplaySettings
                        labels={labels}
                        style={editorWidget.style || {}}
                        onChange={(patch) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), ...patch } })
                        }
                      />
                      <SegmentedControl
                        label={labels.textAlign}
                        value={editorWidget.style?.textAlign || "left"}
                        options={[
                          { value: "left", label: labels.alignLeft },
                          { value: "center", label: labels.alignCenter },
                          { value: "right", label: labels.alignRight },
                        ]}
                        onChange={(textAlign) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), textAlign } })
                        }
                      />
                    </div>
                  ) : null}
                  {editorWidget.type === "table" || editorWidget.type === "freeTable" ? (
                    <div className="datox-react-dashboard__chart-design-editor">
                      <EditorSection title={labels.tableAppearance}>
                        <SegmentedControl
                          label={labels.tableDensity}
                          value={editorWidget.style?.density || "normal"}
                          options={[
                            { value: "compact", label: labels.fontCompact },
                            { value: "normal", label: labels.fontNormal },
                            { value: "large", label: labels.fontLarge },
                          ]}
                          onChange={(density) =>
                            updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), density } })
                          }
                        />
                        <ToggleRow
                          label={labels.showTableHeader}
                          checked={editorWidget.style?.showHeader !== false}
                          onChange={(showHeader) =>
                            updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), showHeader } })
                          }
                        />
                        <ToggleRow
                          label={labels.stripedRows}
                          checked={Boolean(editorWidget.style?.stripedRows)}
                          onChange={(stripedRows) =>
                            updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), stripedRows } })
                          }
                        />
                        <ToggleRow
                          label={labels.headerFill}
                          checked={
                            editorWidget.style?.headerFill !== false &&
                            editorWidget.data?.freeStyle?.highlightHeaderRow !== false
                          }
                          onChange={(headerFill) =>
                            updateWidget(editorWidget.id, {
                              style: { ...(editorWidget.style || {}), headerFill },
                              ...(editorWidget.data?.freeStyle
                                ? {
                                    data: {
                                      ...editorWidget.data,
                                      freeStyle: { ...editorWidget.data.freeStyle, highlightHeaderRow: headerFill },
                                    },
                                  }
                                : {}),
                            })
                          }
                        />
                        {editorWidget.data?.freeStyle ? (
                          <ToggleRow
                            label={labels.showRowNumbers}
                            checked={Boolean(editorWidget.data.freeStyle.showRowNumbers)}
                            onChange={(showRowNumbers) =>
                              updateEditorWidgetData({ freeStyle: { ...editorWidget.data.freeStyle, showRowNumbers } })
                            }
                          />
                        ) : null}
                      </EditorSection>
                      {editorWidget.type === "table" ? (
                        <TableSettingsEditor
                          key={editorWidget.id}
                          data={editorWidget.data || {}}
                          headers={editorWidgetVisibleColumns}
                          table={editorWidgetTable}
                          labels={labels}
                          onChange={updateEditorWidgetData}
                        />
                      ) : null}
                      {!(editorWidget.type === "freeTable" && editorWidget.data?.freeStyle) ? (
                        <div className="datox-react-dashboard__cell-rules-header">
                          <div className="datox-react-dashboard__cell-rules-header-label">Conditional colors</div>
                          <button
                            className="datox-react-dashboard__button datox-react-dashboard__button--outline-primary"
                            type="button"
                            onClick={() =>
                              openEditorCellColorRule(createEditorCellColorRule(editorWidgetVisibleColumns[0] || ""))
                            }
                          >
                            <span className="datox-react-dashboard__cell-rules-add-button-content">
                              <TbPlus size={14} />
                              <span>Add rule</span>
                            </span>
                          </button>
                        </div>
                      ) : null}
                      {!(editorWidget.type === "freeTable" && editorWidget.data?.freeStyle) &&
                        (editorCellColorRules.length ? (
                          <div className="datox-react-dashboard__cell-rules-list">
                            {editorCellColorRules.map((rule, index) => {
                              const operatorLabel =
                                CELL_COLOR_OPERATOR_OPTIONS.find((option) => option.value === rule.operator)?.label ||
                                "="
                              return (
                                <div key={rule.id} className="datox-react-dashboard__cell-rule-card">
                                  <span
                                    aria-hidden
                                    className="datox-react-dashboard__cell-rule-swatch"
                                    style={{ background: rule.color || "#dbeafe" }}
                                  />
                                  <div className="datox-react-dashboard__cell-rule-body">
                                    <div className="datox-react-dashboard__cell-rule-text">
                                      {rule.column || "Column"} {operatorLabel} {String(rule.value ?? "") || "(blank)"}
                                    </div>
                                    <div className="datox-react-dashboard__cell-rule-subtitle datox-react-dashboard__muted-text">
                                      {rule.highlight_row === true ? "Entire row" : "Cell only"} ·{" "}
                                      {rule.color || "#dbeafe"}
                                    </div>
                                  </div>
                                  <div className="datox-react-dashboard__cell-rule-actions-group">
                                    {/* <div className="datox-react-dashboard__cell-rule-actions-row">
                                      <button
                                        className="datox-react-dashboard__widget-menu"
                                        type="button"
                                        aria-label="Move rule up"
                                        title="Move rule up"
                                        disabled={index === 0}
                                        onClick={() => moveEditorCellColorRule(rule.id, "up")}
                                      >
                                        <TbArrowNarrowUp size={16} />
                                      </button>
                                      <button
                                        className="datox-react-dashboard__widget-menu"
                                        type="button"
                                        aria-label="Move rule down"
                                        title="Move rule down"
                                        disabled={index === editorCellColorRules.length - 1}
                                        onClick={() => moveEditorCellColorRule(rule.id, "down")}
                                      >
                                        <TbArrowNarrowDown size={16} />
                                      </button>
                                    </div> */}
                                    <div className="datox-react-dashboard__cell-rule-actions-row">
                                      <button
                                        className="datox-react-dashboard__widget-menu"
                                        type="button"
                                        aria-label="Edit condition"
                                        onClick={() => openEditorCellColorRule({ ...rule })}
                                      >
                                        <TbEdit size={16} />
                                      </button>
                                      <button
                                        className="datox-react-dashboard__widget-menu"
                                        type="button"
                                        aria-label="Remove condition"
                                        onClick={() => removeEditorCellColorRule(rule.id)}
                                      >
                                        <TbTrash size={16} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="datox-react-dashboard__muted-text">No conditional colors configured.</div>
                        ))}
                    </div>
                  ) : null}
                  {editorWidget.type === "filter" ? (
                    <div className="datox-react-dashboard__chart-design-editor">
                      <SegmentedControl
                        label={labels.filterDisplay}
                        value={editorWidget.style?.display || "chips"}
                        options={[
                          { value: "chips", label: labels.filterDisplayChips },
                          { value: "list", label: labels.filterDisplayList },
                          { value: "dropdown", label: labels.filterDisplayDropdown },
                        ]}
                        onChange={(display) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), display } })
                        }
                      />
                      <ToggleRow
                        label={labels.allowMultipleValues}
                        checked={editorWidget.style?.multi !== false}
                        onChange={(multi) =>
                          updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), multi } })
                        }
                      />
                    </div>
                  ) : null}
                  {isChartWidget(editorWidget.type) ? (
                    <div className="datox-react-dashboard__chart-design-editor">
                      <div className="datox-react-dashboard__field-label">{labels.chartType}</div>
                      <div className="datox-react-dashboard__chart-type-grid">
                        {[
                          ["bar", labels.widgets.bar],
                          ["line", labels.widgets.line],
                          ["scatter", labels.widgets.scatter],
                          ["pie", labels.widgets.pie],
                          ["treemap", labels.widgets.treemap],
                          ["funnel", labels.widgets.funnel],
                        ].map(([type, label]) => (
                          <button
                            key={type}
                            className={
                              editorWidget.type === type
                                ? "datox-react-dashboard__chart-type-card datox-react-dashboard__chart-type-card--active"
                                : "datox-react-dashboard__chart-type-card"
                            }
                            type="button"
                            onClick={() => changeWidgetType(type)}
                          >
                            <span>{chartTypeIcon(type)}</span>
                            <strong>{label}</strong>
                          </button>
                        ))}
                      </div>
                      {CHART_CONTROLS[editorWidget.type]?.includes("orientation") ||
                      editorWidget.type === "pie" ||
                      CHART_CONTROLS[editorWidget.type]?.includes("colors") ||
                      (CHART_CONTROLS[editorWidget.type]?.includes("stack") &&
                        editorWidget.data?.measures?.length > 1) ? (
                        <EditorSection title={labels.chartSection} open>
                          {CHART_CONTROLS[editorWidget.type]?.includes("orientation") ? (
                            <SegmentedControl
                              label={labels.chartOrientation}
                              value={editorWidget.style?.orientation || "vertical"}
                              options={[
                                { value: "vertical", label: labels.vertical },
                                { value: "horizontal", label: labels.horizontal },
                              ]}
                              onChange={(orientation) =>
                                updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), orientation } })
                              }
                            />
                          ) : null}
                          {editorWidget.type === "pie" ? (
                            <ToggleRow
                              label={labels.donutChart}
                              checked={editorWidget.style?.donut !== false}
                              onChange={(donut) =>
                                updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), donut } })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("colors") ? (
                            <ToggleRow
                              label={labels.distinctBarColors}
                              checked={Boolean(editorWidget.style?.useDistinctBarColors)}
                              onChange={(useDistinctBarColors) =>
                                updateWidget(editorWidget.id, {
                                  style: { ...editorWidget.style, useDistinctBarColors },
                                })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("stack") &&
                          editorWidget.data?.measures?.length > 1 ? (
                            <ToggleRow
                              label={labels.stackSeries}
                              checked={Boolean(editorWidget.style?.stackSeries)}
                              onChange={(stackSeries) =>
                                updateWidget(editorWidget.id, { style: { ...editorWidget.style, stackSeries } })
                              }
                            />
                          ) : null}
                        </EditorSection>
                      ) : null}
                      {CHART_CONTROLS[editorWidget.type]?.some((control) =>
                        ["legend", "values", "adjacent"].includes(control),
                      ) ? (
                        <EditorSection title={labels.labelsSection}>
                          {CHART_CONTROLS[editorWidget.type]?.includes("legend") ? (
                            <ToggleRow
                              label={labels.showLegend}
                              checked={editorWidget.style?.showLegend !== false}
                              onChange={(showLegend) =>
                                updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), showLegend } })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("values") ? (
                            <ToggleRow
                              label={labels.showValueLabels}
                              checked={
                                editorWidget.type === "pie"
                                  ? editorWidget.style?.showValueLabels !== false
                                  : Boolean(editorWidget.style?.showValueLabels)
                              }
                              onChange={(showValueLabels) =>
                                updateWidget(editorWidget.id, {
                                  style: { ...(editorWidget.style || {}), showValueLabels },
                                })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("adjacent") ? (
                            <ToggleRow
                              label={labels.showValuesNextToLabels}
                              checked={Boolean(editorWidget.style?.showValuesNextToLabels)}
                              onChange={(showValuesNextToLabels) =>
                                updateWidget(editorWidget.id, {
                                  style: { ...(editorWidget.style || {}), showValuesNextToLabels },
                                })
                              }
                            />
                          ) : null}
                        </EditorSection>
                      ) : null}
                      {CHART_CONTROLS[editorWidget.type]?.includes("axes") ? (
                        <EditorSection title={labels.chartAxes}>
                          {CHART_CONTROLS[editorWidget.type]?.includes("axes") ? (
                            <ToggleRow
                              label={labels.showAxisLabels}
                              checked={editorWidget.style?.showValueAxisLabels !== false}
                              onChange={(showValueAxisLabels) =>
                                updateWidget(editorWidget.id, {
                                  style: { ...(editorWidget.style || {}), showValueAxisLabels },
                                })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("grid") ? (
                            <ToggleRow
                              label={labels.showGridLines}
                              checked={editorWidget.style?.showGrid !== false}
                              onChange={(showGrid) =>
                                updateWidget(editorWidget.id, { style: { ...(editorWidget.style || {}), showGrid } })
                              }
                            />
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("titles") ? (
                            <label>
                              <span>{labels.xAxisLabel}</span>
                              <input
                                className="datox-react-dashboard__input"
                                value={editorWidget.style?.xAxisLabel || ""}
                                onChange={(event) =>
                                  updateWidget(editorWidget.id, {
                                    style: { ...(editorWidget.style || {}), xAxisLabel: event.currentTarget.value },
                                  })
                                }
                              />
                            </label>
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("titles") ? (
                            <label>
                              <span>{labels.yAxisLabel}</span>
                              <input
                                className="datox-react-dashboard__input"
                                value={editorWidget.style?.yAxisLabel || ""}
                                onChange={(event) =>
                                  updateWidget(editorWidget.id, {
                                    style: { ...(editorWidget.style || {}), yAxisLabel: event.currentTarget.value },
                                  })
                                }
                              />
                            </label>
                          ) : null}
                        </EditorSection>
                      ) : null}
                      {CHART_CONTROLS[editorWidget.type]?.includes("format") ? (
                        <EditorSection title={labels.numberFormatting}>
                          {CHART_CONTROLS[editorWidget.type]?.includes("format") ? (
                            <label>
                              <span>{labels.valueFormat}</span>
                              <select
                                className="datox-react-dashboard__select"
                                value={editorWidget.style?.valueFormat || "number"}
                                onChange={(event) =>
                                  updateWidget(editorWidget.id, {
                                    style: { ...(editorWidget.style || {}), valueFormat: event.currentTarget.value },
                                  })
                                }
                              >
                                <option value="number">{labels.numberFormat}</option>
                                <option value="integer">{labels.integerFormat}</option>
                                <option value="percent">{labels.percentFormat}</option>
                              </select>
                            </label>
                          ) : null}
                          {CHART_CONTROLS[editorWidget.type]?.includes("format") ? (
                            <label>
                              <span>{labels.decimalPlaces}</span>
                              <EditorNumberInput
                                min={0}
                                max={6}
                                value={
                                  editorWidget.style?.valueFormat === "integer"
                                    ? 0
                                    : (editorWidget.style?.decimalPlaces ?? 2)
                                }
                                disabled={editorWidget.style?.valueFormat === "integer"}
                                onChange={(decimalPlaces) =>
                                  updateWidget(editorWidget.id, { style: { ...editorWidget.style, decimalPlaces } })
                                }
                              />
                            </label>
                          ) : null}
                          {editorWidget.style?.valueFormat === "percent" ? (
                            <p className="datox-react-dashboard__settings-help">{labels.percentFormatHelp}</p>
                          ) : null}
                        </EditorSection>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </aside>
        ) : null}
      </div>
      <div className="datox-react-dashboard__statusbar">
        <span>{labels.widgetCount.replace("{count}", String(widgets.length))}</span>
        <span>{labels.tabCount.replace("{count}", String(config.tabs?.length || 1))}</span>
        <DashboardSaveStatus store={props.saveStatusStore} labels={labels} />
      </div>
      {createOpen ? (
        <DashboardDialog title={labels.createDashboard} onClose={() => setCreateOpen(false)}>
          <form className="datox-react-dashboard__editor-section" onSubmit={submitCreate}>
            <label className="datox-react-dashboard__dialog-field">
              <span>{labels.dashboardName}</span>
              <input
                autoFocus
                placeholder={labels.dashboardNamePlaceholder}
                value={newName}
                onChange={(event) => setNewName(event.currentTarget.value)}
              />
            </label>
            <div className="datox-react-dashboard__dialog-actions">
              <button className="datox-react-dashboard__button" type="button" onClick={() => setCreateOpen(false)}>
                {labels.cancel}
              </button>
              <button
                className="datox-react-dashboard__button datox-react-dashboard__button--primary"
                type="submit"
                disabled={!newName.trim()}
              >
                {labels.createDashboard}
              </button>
            </div>
          </form>
        </DashboardDialog>
      ) : null}
      {renameState ? (
        <DashboardDialog title={renameState.title || labels.rename} onClose={() => setRenameState(null)}>
          <form onSubmit={submitRename}>
            <label className="datox-react-dashboard__dialog-field">
              <span>{labels.name}</span>
              <input
                autoFocus
                placeholder={labels.name}
                value={renameState.name}
                onChange={(event) => {
                  const value = event.currentTarget.value
                  setRenameState((current) => (current ? { ...current, name: value } : current))
                }}
              />
            </label>
            <div className="datox-react-dashboard__dialog-actions">
              <button className="datox-react-dashboard__button" type="button" onClick={() => setRenameState(null)}>
                {labels.cancel}
              </button>
              <button
                className="datox-react-dashboard__button datox-react-dashboard__button--primary"
                type="submit"
                disabled={!renameState.name.trim()}
              >
                {labels.rename}
              </button>
            </div>
          </form>
        </DashboardDialog>
      ) : null}
      {clientAccessOpen ? (
        <DashboardDialog title={labels.clients} onClose={() => setClientAccessOpen(false)}>
          <div className="datox-react-dashboard__client-config">
            <p className="datox-react-dashboard__dialog-copy">{labels.clientsDescription}</p>
            <label className="datox-react-dashboard__dialog-field">
              <span>{labels.clientTable}</span>
              <select
                className="datox-react-dashboard__select"
                value={clientDatasetID}
                onChange={(event) => updateClientAccess({ dataset_id: event.currentTarget.value })}
              >
                {(props.datasets || []).map((dataset) => (
                  <option key={dataset.id} value={dataset.id}>
                    {dataset.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="datox-react-dashboard__dialog-field">
              <span>{labels.clientColumn}</span>
              <select
                className="datox-react-dashboard__select"
                value={clientColumn}
                onChange={(event) => updateClientAccess({ column: event.currentTarget.value })}
              >
                {clientColumns.map((column) => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </select>
            </label>
            <div className="datox-react-dashboard__metadata-card" aria-label={labels.clients}>
              <div className="datox-react-dashboard__metadata-tile">
                <span>{labels.clients}</span>
                <strong>{clientValues.length}</strong>
              </div>
              <div className="datox-react-dashboard__metadata-tile">
                <span>{labels.column}</span>
                <strong>{clientColumn || "-"}</strong>
              </div>
            </div>
            <div className="datox-react-dashboard__client-values">
              <div className="datox-react-dashboard__field-label">{labels.clientValues}</div>
              {clientValues.length ? (
                <div className="datox-react-dashboard__client-values-list">
                  {clientValues.map((value) => (
                    <span key={value} className="datox-react-dashboard__client-value-chip">
                      {value}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="datox-react-dashboard__muted-text">{labels.noClientValues}</p>
              )}
            </div>
            <div className="datox-react-dashboard__dialog-actions">
              <button
                className="datox-react-dashboard__button datox-react-dashboard__button--primary"
                type="button"
                onClick={() => setClientAccessOpen(false)}
              >
                {labels.done}
              </button>
            </div>
          </div>
        </DashboardDialog>
      ) : null}
      {fullViewWidget ? (
        <DashboardDialog
          title={fullViewWidget.title || widgetLabel(labels, fullViewWidget.type)}
          onClose={() => setFullViewWidgetId(null)}
          wide
        >
          <div className="datox-react-dashboard__full-view-widget">
            <WidgetContent
              labels={labels}
              widget={fullViewWidget}
              filterWidgets={activeFilterWidgets}
              table={applyWidgetTableSettings(
                applyDashboardFilters(
                  props.outputTables.find((table) => table.datasetID === fullViewWidget.data?.dataset_id),
                  fullViewWidget,
                  activeFilterWidgets,
                ),
                isChartWidget(fullViewWidget.type)
                  ? { ...fullViewWidget.data, columns: undefined }
                  : fullViewWidget.data,
              )}
              accent={fullViewWidget.style?.accent || config.design?.accent || "#2f80ed"}
              font={config.design?.bodyFont}
            />
          </div>
        </DashboardDialog>
      ) : null}
      {sourceWidget ? (
        <KpiSourcePanel
          widget={sourceWidget}
          filterWidgets={activeFilterWidgets}
          labels={labels}
          onClose={() => setSourceWidget(null)}
        />
      ) : null}
      {previewOpen ? (
        <DashboardDialog title={labels.preview} onClose={() => setPreviewOpen(false)} wide preview>
          <div className="datox-react-dashboard__dialog-actions" style={{ marginTop: 0, marginBottom: 16 }}>
            <button
              className="datox-react-dashboard__button datox-react-dashboard__button--primary"
              type="button"
              onClick={() => {
                setPreviewOpen(false)
                setPdfPreviewOpen(true)
              }}
            >
              {labels.exportPdf}
            </button>
          </div>
          <div className="datox-react-dashboard__preview-shell">
            <div className="datox-react-dashboard__published-tabs datox-react-dashboard__preview-tabs">
              {(config.tabs || []).map((tab) => (
                <button
                  key={tab.id}
                  className={
                    tab.id === activeTab.id
                      ? "datox-react-dashboard__published-tab datox-react-dashboard__published-tab--active"
                      : "datox-react-dashboard__published-tab"
                  }
                  type="button"
                  onClick={() => setActiveTabId(tab.id)}
                >
                  {tab.name || labels.defaultTabName}
                </button>
              ))}
            </div>
            <div
              className="datox-react-dashboard__page-wrap"
              style={{ width: (config.canvas?.width || 1123) + 56, minWidth: (config.canvas?.width || 1123) + 56 }}
            >
              <div
                className={`datox-react-dashboard__page ${config.design?.lightGrayBackground ? "datox-react-dashboard__page--gray" : ""}`}
                style={{
                  "--ds-accent": config.design?.accent || "#2f80ed",
                  "--ds-header-color": config.design?.headerColor || "#101828",
                  "--ds-heading-font": designFont(config.design?.headingFont),
                  "--ds-body-font": designFont(config.design?.bodyFont),
                  width: config.canvas?.width || 1123,
                  minHeight: dashboardKpiPageHeight(config.canvas?.height, widgets),
                }}
              >
                {widgets.map((widget) => (
                  <div
                    key={widget.id}
                    style={{
                      position: "absolute",
                      left: widget.layout.x,
                      top: widget.layout.y,
                      width: widget.layout.w,
                      height: dashboardWidgetHeight(widget),
                    }}
                  >
                    <WidgetFrame
                      preview
                      labels={labels}
                      filterWidgets={activeFilterWidgets}
                      widget={widget}
                      table={applyDashboardFilters(
                        props.outputTables.find((table) => table.datasetID === widget.data?.dataset_id),
                        widget,
                        activeFilterWidgets,
                      )}
                      accent={config.design?.accent || "#2f80ed"}
                      design={config.design || {}}
                      comparison={comparisonState.itemsByWidgetId[widget.id]}
                      selected={false}
                      menuOpen={false}
                      onToggleMenu={() => {}}
                      onRename={() => {}}
                      onEditData={() => {}}
                      onEditDesign={() => {}}
                      onExportTable={() => {}}
                      onFullView={() => {}}
                      onFilterValueToggle={(value) => toggleFilterValue(widget, value)}
                      onDelete={() => {}}
                      onDuplicate={() => {}}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DashboardDialog>
      ) : null}
      {cellColorRulesOpen &&
      cellColorRuleDraft &&
      editorWidget &&
      (editorWidget.type === "table" || editorWidget.type === "freeTable") ? (
        <DashboardDialog
          title={
            editorCellColorRules.some((rule) => rule.id === cellColorRuleDraft.id)
              ? "Edit color rule"
              : "Add color rule"
          }
          onClose={closeEditorCellColorRule}
        >
          <form
            className="datox-react-dashboard__cell-rules-modal-form"
            onSubmit={(event) => {
              event.preventDefault()
              saveEditorCellColorRule()
            }}
          >
            <label className="datox-react-dashboard__dialog-field">
              <span>Column</span>
              <select
                className="datox-react-dashboard__select datox-react-dashboard__cell-rule-control"
                value={cellColorRuleDraft.column || ""}
                onChange={(event) => updateEditorCellColorRule({ column: event.currentTarget.value })}
              >
                <option value="">Select column</option>
                {editorWidgetVisibleColumns.map((column) => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </select>
            </label>
            <label className="datox-react-dashboard__dialog-field">
              <span>Condition</span>
              <select
                className="datox-react-dashboard__select datox-react-dashboard__cell-rule-control"
                value={cellColorRuleDraft.operator || "equals"}
                onChange={(event) => updateEditorCellColorRule({ operator: event.currentTarget.value })}
              >
                {CELL_COLOR_OPERATOR_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="datox-react-dashboard__dialog-field">
              <span>Value</span>
              <input
                className="datox-react-dashboard__input datox-react-dashboard__cell-rule-control"
                value={cellColorRuleDraft.value ?? ""}
                onChange={(event) => updateEditorCellColorRule({ value: event.currentTarget.value })}
              />
            </label>
            <label className="datox-react-dashboard__toggle-row datox-react-dashboard__cell-rules-toggle-row">
              <input
                type="checkbox"
                checked={cellColorRuleDraft.highlight_row === true}
                onChange={(event) => updateEditorCellColorRule({ highlight_row: event.currentTarget.checked })}
              />
              <span>Highlight entire row</span>
            </label>
            <div className="datox-react-dashboard__cell-rules-helper datox-react-dashboard__muted-text">
              Off colors only the selected column. First matching rule wins for each cell.
            </div>
            <label className="datox-react-dashboard__dialog-field">
              <span>{cellColorRuleDraft.highlight_row === true ? "Row color" : "Cell color"}</span>
              <input
                value={cellColorRuleDraft.color || "#dbeafe"}
                type="color"
                onChange={(event) => updateEditorCellColorRule({ color: event.currentTarget.value })}
                aria-label="Rule color"
                className="datox-react-dashboard__cell-rule-color-input"
              />
            </label>
            <div className="datox-react-dashboard__dialog-actions datox-react-dashboard__cell-rules-modal-actions">
              <button className="datox-react-dashboard__button" type="button" onClick={closeEditorCellColorRule}>
                Cancel
              </button>
              <button className="datox-react-dashboard__button datox-react-dashboard__button--primary" type="submit">
                Save rule
              </button>
            </div>
          </form>
        </DashboardDialog>
      ) : null}
      {deleteDashboardOpen ? (
        <DashboardDialog title={labels.deleteDashboard} onClose={() => setDeleteDashboardOpen(false)}>
          <p className="datox-react-dashboard__dialog-copy">{labels.deleteDashboardConfirm}</p>
          <div className="datox-react-dashboard__dialog-actions">
            <button
              className="datox-react-dashboard__button"
              type="button"
              onClick={() => setDeleteDashboardOpen(false)}
            >
              {labels.cancel}
            </button>
            <button
              className="datox-react-dashboard__button datox-react-dashboard__button--danger-solid"
              type="button"
              onClick={() => {
                setDeleteDashboardOpen(false)
                props.onDeleteDashboard?.()
              }}
            >
              {labels.deleteDashboard}
            </button>
          </div>
        </DashboardDialog>
      ) : null}
      {publishedLink ? (
        <DashboardDialog
          title={labels.publishDashboard}
          onClose={() => {
            setPublishedLink("")
            setPublishedLinkCopied(false)
          }}
        >
          <p className="datox-react-dashboard__dialog-copy">{labels.publishDashboardCopied}</p>
          <div className="datox-react-dashboard__copy-link-row">
            <input className="datox-react-dashboard__input" value={publishedLink} readOnly />
            <button
              className={
                publishedLinkCopied
                  ? "datox-react-dashboard__toolbar-button datox-react-dashboard__toolbar-button--success"
                  : "datox-react-dashboard__toolbar-button"
              }
              type="button"
              title={publishedLinkCopied ? labels.copied : labels.copyLink}
              onClick={copyPublishedLink}
            >
              {publishedLinkCopied ? "✓" : "⧉"}
            </button>
          </div>
          <div className="datox-react-dashboard__dialog-actions">
            <button
              className="datox-react-dashboard__button"
              type="button"
              onClick={() => window.open(publishedLink, "_blank", "noopener,noreferrer")}
            >
              {labels.openLink}
            </button>
            <button
              className="datox-react-dashboard__button datox-react-dashboard__button--primary"
              type="button"
              onClick={() => {
                setPublishedLink("")
                setPublishedLinkCopied(false)
              }}
            >
              {labels.done}
            </button>
          </div>
        </DashboardDialog>
      ) : null}
      {shareAccessOpen ? (
        <DashboardDialog title={labels.shareAccess} onClose={() => setShareAccessOpen(false)} reviewer>
          <form onSubmit={submitShareAccess} className="datox-react-dashboard__share-form">
            <div className="datox-react-dashboard__reviewer-table">
              <div className="datox-react-dashboard__reviewer-row datox-react-dashboard__reviewer-row--head">
                <span />
                <strong>{labels.reviewerName}</strong>
                <strong>{labels.reviewerEmail}</strong>
                <strong>{labels.client}</strong>
                <strong>{labels.reports}</strong>
                <strong>{labels.status}</strong>
                <span />
              </div>
              {shareReviewers.map((reviewer) => (
                <div key={reviewer.id} className="datox-react-dashboard__reviewer-row">
                  <input
                    type="checkbox"
                    checked={reviewer.selected !== false}
                    onChange={(event) => updateShareReviewer(reviewer.id, { selected: event.currentTarget.checked })}
                  />
                  <input
                    value={reviewer.name}
                    onChange={(event) => updateShareReviewer(reviewer.id, { name: event.currentTarget.value })}
                  />
                  <input
                    value={reviewer.email}
                    placeholder={labels.reviewersPlaceholder}
                    onChange={(event) => updateShareReviewerEmail(reviewer.id, event.currentTarget.value)}
                  />
                  <select
                    className="datox-react-dashboard__select"
                    value={reviewer.clientValue || ""}
                    disabled={!clientColumn || clientValues.length === 0}
                    onChange={(event) => updateShareReviewer(reviewer.id, { clientValue: event.currentTarget.value })}
                  >
                    <option value="">{labels.allClients}</option>
                    {clientValues.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>{config.title || labels.dashboardTitle}</span>
                  <span>{reviewer.status || labels.notSent}</span>
                  <button type="button" onClick={() => removeShareReviewer(reviewer)}>
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              className="datox-react-dashboard__button datox-react-dashboard__button--outline-primary"
              type="button"
              onClick={addShareReviewer}
            >
              + {labels.addAnotherReviewer}
            </button>
            <div className="datox-react-dashboard__dialog-actions">
              <button className="datox-react-dashboard__button" type="button" onClick={() => setShareAccessOpen(false)}>
                {labels.cancel}
              </button>
              <button
                className="datox-react-dashboard__button datox-react-dashboard__button--primary"
                type="submit"
                disabled={
                  shareAccessSending ||
                  hasDuplicateReviewerEmails(shareReviewers) ||
                  !shareReviewers.some((reviewer) => reviewer.selected !== false && reviewer.email.trim())
                }
              >
                {shareAccessSending ? labels.sendingShareAccess : labels.sendShareAccess}
              </button>
            </div>
          </form>
        </DashboardDialog>
      ) : null}
      {sourceExplanation ? (
        <DashboardDialog title={labels.source} onClose={closeSourceExplanation} wide>
          <div className="datox-react-dashboard__source-explanation">
            {sourceExplanation.loading ? (
              <div className="datox-react-dashboard__source-loading" role="status" aria-live="polite">
                <span className="datox-react-dashboard__source-spinner" aria-hidden="true" />
                <strong>{labels.loading}</strong>
                <div className="datox-react-dashboard__source-loading-lines" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="datox-react-dashboard__source-loading-card" aria-hidden="true">
                  <span />
                  <span />
                </div>
              </div>
            ) : null}

            {sourceExplanation.data ? (
              <>
                <h3>{sourceExplanation.data.title}</h3>
                <DashboardMarkdown
                  className="datox-react-dashboard__source-summary"
                  text={sourceExplanation.data.summary_markdown}
                />
                <dl className="datox-react-dashboard__source-metadata">
                  <dt>{labels.outputTable}</dt>
                  <dd>{sourceExplanation.data.dataset_name}</dd>
                </dl>
                {sourceExplanation.data.formula_markdown ? (
                  <DashboardMarkdown
                    className="datox-react-dashboard__source-formula"
                    text={sourceExplanation.data.formula_markdown}
                  />
                ) : null}
                {sourceExplanation.data.filters?.length ? (
                  <section>
                    <h4>{labels.filter}</h4>
                    <pre className="datox-react-dashboard__source-formula">
                      {JSON.stringify(sourceExplanation.data.filters, null, 2)}
                    </pre>
                  </section>
                ) : null}
                {sourceExplanation.data.sources.map((source, index) => {
                  const columns = sourceRowColumns(source.sample_rows)
                  return (
                    <article
                      className="datox-react-dashboard__source-card"
                      key={`${source.source_file_id || "unknown"}:${source.source_extracted_table || index}`}
                    >
                      <strong>{source.source_file_name || source.source_extracted_table || labels.notAssessed}</strong>
                      {source.source_file_name && source.source_extracted_table ? (
                        <div>{source.source_extracted_table}</div>
                      ) : null}
                      {source.source_file_id && props.onOpenEvidence ? (
                        <button
                          className="datox-react-dashboard__button datox-react-dashboard__source-open"
                          type="button"
                          onClick={() =>
                            props.onOpenEvidence({
                              fileID: source.source_file_id,
                              fileName: source.source_file_name,
                              extractedTable: source.source_extracted_table,
                              evidence: source.evidence || [],
                            })
                          }
                        >
                          {labels.openFile}
                        </button>
                      ) : null}
                      {columns.length ? (
                        <div className="datox-react-dashboard__source-table-wrap">
                          <table
                            className="datox-react-dashboard__source-table"
                            style={{ minWidth: `${Math.max(720, columns.length * 90)}px` }}
                          >
                            <thead>
                              <tr>
                                {columns.map((column) => (
                                  <th key={column}>{column}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {source.sample_rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                  {columns.map((column) => (
                                    <td key={column}>{formatSourceValue(row[column])}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </article>
                  )
                })}
              </>
            ) : null}
            <div className="datox-react-dashboard__dialog-actions">
              <button className="datox-react-dashboard__button" type="button" onClick={closeSourceExplanation}>
                {labels.done}
              </button>
            </div>
          </div>
        </DashboardDialog>
      ) : null}
      {snapshotOptionsOpen ? (
        <DashboardDialog title={labels.exportPdf} onClose={() => setSnapshotOptionsOpen(false)}>
          <div className="datox-react-dashboard__pdf-options">
            <fieldset>
              <legend>{labels.pdf.scope}</legend>
              <label>
                <input
                  type="checkbox"
                  checked={snapshotTabIds.length === (config.tabs || []).length}
                  onChange={(event) =>
                    setSnapshotTabIds(event.currentTarget.checked ? (config.tabs || []).map((tab) => tab.id) : [])
                  }
                />
                <span>{labels.pdf.allTabs}</span>
              </label>
              {(config.tabs || []).map((tab) => (
                <label key={tab.id}>
                  <input
                    type="checkbox"
                    checked={snapshotTabIds.includes(tab.id)}
                    onChange={(event) =>
                      setSnapshotTabIds((current) =>
                        event.currentTarget.checked
                          ? [...new Set([...current, tab.id])]
                          : current.filter((tabId) => tabId !== tab.id),
                      )
                    }
                  />
                  <span>{tab.name || labels.defaultTabName}</span>
                </label>
              ))}
            </fieldset>
            <label className="datox-react-dashboard__pdf-check">
              <input
                type="checkbox"
                checked={snapshotFullTables}
                onChange={(event) => setSnapshotFullTables(event.currentTarget.checked)}
              />
              <span>{labels.pdf.allRows}</span>
            </label>
            <p className="datox-react-dashboard__muted-text">{labels.pdf.allRowsHelp}</p>
            <div className="datox-react-dashboard__dialog-actions">
              <button
                className="datox-react-dashboard__button"
                type="button"
                onClick={() => setSnapshotOptionsOpen(false)}
              >
                {labels.cancel}
              </button>
              <button
                className="datox-react-dashboard__button datox-react-dashboard__button--primary"
                type="button"
                disabled={exportingSnapshot || !snapshotTabIds.length}
                onClick={() => void exportDashboardSnapshot()}
              >
                {exportingSnapshot ? labels.pdf.exporting : labels.exportPdf}
              </button>
            </div>
          </div>
        </DashboardDialog>
      ) : null}
      {pdfPreviewOpen ? (
        <DashboardPdfPreview
          config={config}
          activeTabID={activeTab.id}
          dashboardTitle={labels.dashboardTitle}
          labels={labels.pdf}
          tables={props.outputTables}
          onClose={() => setPdfPreviewOpen(false)}
          onLoadTables={props.onLoadPdfTables}
          onExport={props.onExportDashboardPdf}
        />
      ) : null}
      {/* {exportingPdf ? (
        <div ref={exportRef} className="datox-react-dashboard__export-root" aria-hidden="true">
          {(config.tabs || []).map((tab) => {
            const tabWidgets = tab.widgets || []
            const tabFilterWidgets = tabWidgets.filter(
              (widget) => widget.type === "filter" && widget.data?.selectedValues?.length,
            )
            return (
              <div
                key={tab.id}
                className={`datox-react-dashboard__page datox-react-dashboard__export-page ${config.design?.lightGrayBackground ? "datox-react-dashboard__page--gray" : ""}`}
                style={{
                  "--ds-accent": config.design?.accent || "#2f80ed",
                  "--ds-header-color": config.design?.headerColor || "#101828",
                  "--ds-heading-font": designFont(config.design?.headingFont),
                  "--ds-body-font": designFont(config.design?.bodyFont),
                  width: config.canvas?.width || 1123,
                  minHeight: dashboardKpiPageHeight(config.canvas?.height, tabWidgets),
                }}
              >
                {tabWidgets.map((widget) => (
                  <div
                    key={widget.id}
                    style={{
                      position: "absolute",
                      left: widget.layout.x,
                      top: widget.layout.y,
                      width: widget.layout.w,
                      height: dashboardWidgetHeight(widget),
                    }}
                  >
                    <WidgetFrame
                      preview
                      labels={labels}
                      filterWidgets={tabFilterWidgets}
                      widget={widget}
                      table={applyDashboardFilters(
                        props.outputTables.find((table) => table.datasetID === widget.data?.dataset_id),
                        widget,
                        tabFilterWidgets,
                      )}
                      accent={config.design?.accent || "#2f80ed"}
                      design={config.design || {}}
                      selected={false}
                      menuOpen={false}
                      onToggleMenu={() => {}}
                      onRename={() => {}}
                      onEditData={() => {}}
                      onEditDesign={() => {}}
                      onExportTable={() => {}}
                      onFullView={() => {}}
                      onFilterValueToggle={() => {}}
                      onDelete={() => {}}
                      onDuplicate={() => {}}
                    />
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      ) : null} */}
    </div>
  )
}

function DashboardDialog(props) {
  return (
    <div
      className={
        props.preview
          ? "datox-react-dashboard__dialog-backdrop datox-react-dashboard__dialog-backdrop--preview"
          : "datox-react-dashboard__dialog-backdrop"
      }
      role="presentation"
    >
      <div
        className={
          props.preview
            ? "datox-react-dashboard__dialog datox-react-dashboard__dialog--wide datox-react-dashboard__dialog--preview"
            : props.reviewer
              ? "datox-react-dashboard__dialog datox-react-dashboard__dialog--reviewer"
              : props.wide
                ? "datox-react-dashboard__dialog datox-react-dashboard__dialog--wide"
                : "datox-react-dashboard__dialog"
        }
        style={props.style}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
      >
        <div className="datox-react-dashboard__dialog-header">
          <h2>{props.title}</h2>
          <button type="button" onClick={props.onClose}>
            ×
          </button>
        </div>
        {props.children}
      </div>
    </div>
  )
}

function ColorSwatches(props) {
  return (
    <div>
      <div className="datox-react-dashboard__field-label">{props.label}</div>
      <div className="datox-react-dashboard__swatches">
        {props.colors.map((color) => (
          <button
            key={color}
            className={
              props.value === color
                ? "datox-react-dashboard__swatch datox-react-dashboard__swatch--active"
                : "datox-react-dashboard__swatch"
            }
            style={{ background: color }}
            type="button"
            onClick={() => props.onChange(color)}
          />
        ))}
      </div>
    </div>
  )
}

function SegmentedControl(props) {
  return (
    <div>
      <div className="datox-react-dashboard__field-label">{props.label}</div>
      <div className="datox-react-dashboard__mini-segmented">
        {props.options.map((option) => (
          <button
            key={option.value}
            className={
              props.value === option.value
                ? "datox-react-dashboard__mini-segment datox-react-dashboard__mini-segment--active"
                : "datox-react-dashboard__mini-segment"
            }
            type="button"
            onClick={() => props.onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function ToggleRow(props) {
  return (
    <label className="datox-react-dashboard__toggle-row">
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(event) => props.onChange(event.currentTarget.checked)}
      />
      <span>{props.label}</span>
    </label>
  )
}

function WidgetFrame(props) {
  const header = props.widget.type === "header"
  const textOnly = props.widget.type === "text" || props.widget.type === "title"
  const kpiOnly = props.widget.type === "kpi"
  const pdfStyle = props.printing ? props.pdfStyle : undefined
  const effectiveAccent = props.widget.style?.accent || props.accent
  const radiusSetting = props.widget.style?.radius || props.design?.radius || "medium"
  const radius = radiusSetting === "square" ? 0 : radiusSetting === "large" ? 18 : 12
  const displayTable = applyWidgetTableSettings(
    props.table,
    isChartWidget(props.widget.type) ? { ...props.widget.data, columns: undefined } : props.widget.data,
  )
  const compact = props.widget.layout?.h < 150 || props.design?.fontSize === "compact"
  const large = props.design?.fontSize === "large"
  return (
    <div
      className={`datox-react-dashboard__widget ${header ? "datox-react-dashboard__widget--header" : ""} ${compact ? "datox-react-dashboard__widget--compact" : ""} ${large ? "datox-react-dashboard__widget--large" : ""} ${props.design?.widgetShadows ? "datox-react-dashboard__widget--shadow" : ""} ${props.selected ? "datox-react-dashboard__widget--selected" : ""} ${pdfStyle?.wrapTitle ? "datox-react-dashboard__pdf-wrap-text" : ""} ${pdfStyle?.wrapTitle && !header && !textOnly ? "datox-react-dashboard__widget--pdf-wrap" : ""} ${pdfStyle?.contentSize ? "datox-react-dashboard__pdf-content-size" : ""} ${pdfStyle?.titleSize ? "datox-react-dashboard__pdf-title-size" : ""} ${pdfStyle?.wrapCells ? "datox-react-dashboard__pdf-wrap-cells" : ""}`}
      style={{
        "--ds-accent": effectiveAccent,
        "--ds-pdf-content-size": pdfStyle?.contentSize ? `${pdfStyle.contentSize}px` : undefined,
        "--ds-pdf-title-size": pdfStyle?.titleSize ? `${pdfStyle.titleSize}px` : undefined,
        "--ds-header-color": props.design?.headerColor || "#101828",
        borderRadius: header ? 0 : radius,
      }}
    >
      {!props.preview && (header || textOnly) ? (
        <>
          <div
            className="datox-react-dashboard__text-drag-strip datox-react-dashboard__drag-handle"
            aria-hidden="true"
          />
          <div className="datox-react-dashboard__widget-actions datox-react-dashboard__widget-actions--header">
            <WidgetMenu
              labels={props.labels}
              open={props.menuOpen}
              onToggle={props.onToggleMenu}
              onRename={props.onRename}
              onEditData={props.onEditData}
              onEditDesign={props.onEditDesign}
              onExplainSource={props.onExplainSource}
              onExportTable={props.onExportTable}
              onDuplicate={props.onDuplicate}
              onFullView={props.onFullView}
              onExplain={kpiOnly ? props.onExplain : null}
              onDelete={props.onDelete}
            />
          </div>
        </>
      ) : !props.preview ? (
        <div className="datox-react-dashboard__widget-head datox-react-dashboard__drag-handle">
          <div className="datox-react-dashboard__widget-title">{props.widget.title}</div>
          <div className="datox-react-dashboard__widget-head-right">
            {kpiOnly ? (
              <KpiComparisonDisplay
                placement="badge"
                widget={props.widget}
                result={props.comparison}
                labels={props.labels}
              />
            ) : null}
            <WidgetMenu
              labels={props.labels}
              open={props.menuOpen}
              onToggle={props.onToggleMenu}
              onRename={props.onRename}
              onEditData={props.onEditData}
              onEditDesign={props.onEditDesign}
              onExplainSource={props.onExplainSource}
              onExportTable={props.onExportTable}
              onDuplicate={props.onDuplicate}
              onFullView={props.onFullView}
              onExplain={kpiOnly ? props.onExplain : null}
              onDelete={props.onDelete}
            />
          </div>
        </div>
      ) : !header && !textOnly ? (
        <div className="datox-react-dashboard__widget-head">
          <div className="datox-react-dashboard__widget-title">{props.widget.title}</div>
          <div className="datox-react-dashboard__widget-head-right">
            {kpiOnly ? (
              <KpiComparisonDisplay
                placement="badge"
                widget={props.widget}
                result={props.comparison}
                labels={props.labels}
              />
            ) : null}
            {kpiOnly && props.onExplain ? (
              <button type="button" onClick={props.onExplain}>
                {props.labels.kpi.explain}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div
        className={
          header
            ? "datox-react-dashboard__header-content"
            : textOnly
              ? "datox-react-dashboard__widget-body datox-react-dashboard__widget-body--full"
              : kpiOnly
                ? "datox-react-dashboard__widget-body datox-react-dashboard__widget-body--kpi"
                : "datox-react-dashboard__widget-body"
        }
      >
        <WidgetContent
          labels={props.labels}
          printing={props.printing}
          pdfStyle={pdfStyle}
          widget={props.widget}
          table={displayTable}
          accent={effectiveAccent}
          comparison={props.comparison}
          font={props.design?.bodyFont}
          inlineEditing={props.inlineEditing}
          onInlineEdit={props.onInlineEdit}
          onInlineEditClose={props.onInlineEditClose}
          onInlineTextChange={props.onInlineTextChange}
          onFilterValueToggle={props.onFilterValueToggle}
          filterWidgets={props.filterWidgets}
        />
      </div>
    </div>
  )
}

function WidgetMenu(props) {
  return (
    <div className="datox-react-dashboard__widget-actions">
      <button
        className="datox-react-dashboard__widget-menu"
        type="button"
        title={props.labels.more}
        onClick={(event) => {
          event.stopPropagation()
          props.onToggle()
        }}
      >
        ...
      </button>
      {props.open ? (
        <div className="datox-react-dashboard__widget-dropdown">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onRename()
            }}
          >
            {props.labels.renameWidget}
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onEditData()
            }}
          >
            {props.labels.editData}
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onEditDesign()
            }}
          >
            {props.labels.editDesign}
          </button>
          {props.onExplainSource ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                props.onExplainSource()
              }}
            >
              {props.labels.source}
            </button>
          ) : null}
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onExportTable()
            }}
          >
            {props.labels.exportTable}
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onDuplicate()
            }}
          >
            {props.labels.duplicateWidget}
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              props.onFullView()
            }}
          >
            {props.labels.fullView}
          </button>
          {props.onExplain ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                props.onExplain()
              }}
            >
              {props.labels.kpi.explain}
            </button>
          ) : null}
          <button
            type="button"
            className="datox-react-dashboard__widget-dropdown-danger"
            onClick={(event) => {
              event.stopPropagation()
              props.onDelete()
            }}
          >
            {props.labels.deleteWidget}
          </button>
        </div>
      ) : null}
    </div>
  )
}

function WidgetContent(props) {
  const kpi = useContext(DashboardKpiContext)
  if (props.widget.type === "header") return props.widget.title
  if (props.widget.type === "text")
    return (
      <EditableTextContent
        multiline
        value={props.widget.text || ""}
        style={props.widget.style || {}}
        labels={props.labels}
        editing={props.inlineEditing}
        onEdit={props.onInlineEdit}
        onClose={props.onInlineEditClose}
        onChange={props.onInlineTextChange}
      />
    )
  if (props.widget.type === "title")
    return (
      <EditableTextContent
        value={props.widget.title || ""}
        style={props.widget.style || {}}
        labels={props.labels}
        editing={props.inlineEditing}
        onEdit={props.onInlineEdit}
        onClose={props.onInlineEditClose}
        onChange={props.onInlineTextChange}
      />
    )
  if (props.widget.type === "kpi")
    return (
      <KpiWidget
        labels={props.labels}
        valueScale={props.pdfStyle?.valueScale}
        locale={kpi.locale}
        status={
          kpi.datasets[props.widget.data?.dataset_id]?.status || (props.widget.data?.dataset_id ? "error" : "missing")
        }
        table={props.table}
        data={props.widget.data || {}}
        style={props.widget.style || {}}
        layout={{ ...props.widget.layout, h: dashboardWidgetHeight(props.widget) }}
        comparison={props.comparison}
        accent={props.widget.style?.accent || props.accent}
        font={props.font}
        widget={props.widget}
        filterWidgets={props.filterWidgets}
        sourceKey={kpi.sourceKeys?.[props.widget.data?.dataset_id] ?? kpi.sourceKey}
        onEvaluateKpi={kpi.onEvaluateKpi}
      />
    )
  if (props.widget.type === "freeTable" && props.widget.data?.freeStyle)
    return <FreeStyleWidget value={props.widget.data.freeStyle} style={props.widget.style || {}} />
  if (props.widget.type === "table" || props.widget.type === "freeTable")
    return (
      <TableWidget
        key={JSON.stringify([
          props.widget.id,
          props.widget.data?.dataset_id,
          props.widget.data?.filters,
          props.widget.data?.sort,
          props.widget.data?.rowLimit,
          props.widget.data?.pageSize,
          (props.filterWidgets || [])
            .filter((filter) =>
              filter.data?.targets?.some((target) => target.dataset_id === props.widget.data?.dataset_id),
            )
            .map((filter) => filter.data),
        ])}
        table={props.table}
        style={props.widget.style || {}}
        data={props.widget.data || {}}
        labels={props.labels}
        missingLabel={props.widget.id?.startsWith("risk-table-") ? props.labels?.notAssessed : ""}
      />
    )
  if (props.widget.type === "filter")
    return (
      <FilterWidget
        table={props.table}
        label={props.widget.data?.label}
        selectedValues={props.widget.data?.selectedValues || []}
        style={props.widget.style || {}}
        onToggle={props.onFilterValueToggle}
      />
    )
  return (
    <ChartWidget
      labels={props.labels}
      widget={props.widget}
      table={props.table}
      accent={props.accent}
      printing={props.printing}
      textSize={props.pdfStyle?.contentSize}
    />
  )
}

function EditableTextContent(props) {
  if (props.editing)
    return (
      <div className="datox-react-dashboard__inline-edit">
        <textarea
          autoFocus
          className="datox-react-dashboard__inline-edit-input"
          value={props.value}
          onChange={(event) => props.onChange(event.currentTarget.value)}
          onBlur={props.onClose}
          onKeyDown={(event) => {
            if (event.key === "Escape") props.onClose()
          }}
        />
      </div>
    )
  return (
    <div
      className={
        props.multiline
          ? "datox-react-dashboard__editable-text datox-react-dashboard__editable-text--body"
          : "datox-react-dashboard__editable-text datox-react-dashboard__editable-text--title"
      }
      style={{
        alignItems: verticalAlignValue(props.style.verticalAlign),
        justifyContent: verticalAlignValue(props.style.verticalAlign),
        textAlign: props.style.textAlign || "left",
        color: props.style.textColor || "#101828",
        fontSize: props.style.fontSize ? `${props.style.fontSize}px` : undefined,
        fontWeight: props.style.fontWeight || undefined,
      }}
    >
      <span>{props.value}</span>
      <button
        type="button"
        title={props.labels.editText}
        onClick={(event) => {
          event.stopPropagation()
          props.onEdit()
        }}
      >
        ✎
      </button>
    </div>
  )
}

export function aggregateKpiValue(table, data) {
  if (!table) return 0
  const metric = data?.metric
  const aggregation = data?.aggregation || "sum"
  const values = metric ? numericValues(table, metric) : []
  if (aggregation === "count") return table.rows.length
  if (!values.length) return 0
  if (aggregation === "avg") return values.reduce((sum, value) => sum + value, 0) / values.length
  if (aggregation === "min") return Math.min(...values)
  if (aggregation === "max") return Math.max(...values)
  if (aggregation === "first") return values[0]
  if (aggregation === "last") return values[values.length - 1]
  return values.reduce((sum, value) => sum + value, 0)
}

function FreeStyleWidget(props) {
  const value = props.value
  return (
    <div className="datox-react-dashboard__table-container">
      <table
        className={`datox-react-dashboard__table ${props.style?.density === "compact" ? "datox-react-dashboard__table--compact" : props.style?.density === "large" ? "datox-react-dashboard__table--large" : ""} ${props.style?.stripedRows ? "datox-react-dashboard__table--striped" : ""} ${props.style?.headerFill === false || value.highlightHeaderRow === false ? "datox-react-dashboard__table--plain-header" : ""}`}
      >
        <colgroup>
          {value.showRowNumbers ? <col style={{ width: 40 }} /> : null}
          {value.headers.map((_, index) => (
            <col key={index} style={{ width: value.columnWidths?.[index] || 168 }} />
          ))}
        </colgroup>
        {props.style?.showHeader === false ? null : (
          <thead>
            <tr>
              {value.showRowNumbers ? <th /> : null}
              {value.headers.map((header, index) => (
                <th key={index} style={{ minWidth: value.columnWidths?.[index] || 168 }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {value.rows.map((row, index) => (
            <tr
              key={index}
              className={value.rowTypes?.[index] === "section" ? "datox-react-dashboard__section-row" : ""}
            >
              {value.showRowNumbers ? <td>{index + 1}</td> : null}
              {value.headers.map((header, col) => (
                <td key={col} style={{ textAlign: value.cellAlign?.[`${index}:${col}`] || "left" }}>
                  {formattedCell(row[col], { format: value.columnTypes?.[col] || "text", decimals: 2 })}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TableWidget(props) {
  const [page, setPage] = useState(1)
  const viewport = useRef(null)
  const headers = props.table?.headers || []
  const rows = props.table?.rows || []
  const pagination = !props.limit && props.data?.pagination !== false
  const size = Math.max(5, Math.min(1000, Number(props.data?.pageSize) || 20))
  const pages = Math.max(1, Math.ceil(rows.length / size))
  const current = Math.min(page, pages)
  const selectPage = (next) => {
    setPage(Math.max(1, Math.min(pages, next)))
    if (viewport.current) viewport.current.scrollTop = 0
  }
  const displayed = props.limit
    ? rows.slice(0, props.limit)
    : pagination
      ? rows.slice((current - 1) * size, current * size)
      : rows
  const summary = useMemo(
    () => (props.data?.showSummary ? tableSummary(rows, headers, props.data.summaryColumns) : null),
    [rows, headers, props.data?.showSummary, props.data?.summaryColumns],
  )
  const densityClass =
    props.style?.density === "compact"
      ? "datox-react-dashboard__table--compact"
      : props.style?.density === "large"
        ? "datox-react-dashboard__table--large"
        : ""
  const cellColorRules = Array.isArray(props.style?.cellColorRules) ? props.style.cellColorRules : []
  const visibleColumnKeys = useMemo(() => new Set(headers), [headers])

  const getCellConditionalStyle = (row, columnKey) => {
    const matchingRule = cellColorRules.find((rule) => {
      if (!rule?.column || !visibleColumnKeys.has(rule.column)) return false
      if (rule.highlight_row !== true && rule.column !== columnKey) return false
      const cellValue = row[rule.column]
      const operator = rule.operator || "equals"
      const expected = rule.value ?? ""
      const cellNumber = parseComparableNumber(cellValue)
      const expectedNumber = parseComparableNumber(expected)

      if (["gt", "gte", "lt", "lte"].includes(operator)) {
        if (cellNumber == null || expectedNumber == null) return false
        if (operator === "gt") return cellNumber > expectedNumber
        if (operator === "gte") return cellNumber >= expectedNumber
        if (operator === "lt") return cellNumber < expectedNumber
        return cellNumber <= expectedNumber
      }

      const left = String(cellValue ?? "")
        .trim()
        .toLowerCase()
      const right = String(expected ?? "")
        .trim()
        .toLowerCase()
      if (operator === "contains") return right ? left.includes(right) : false
      if (operator === "not_equals") return left !== right
      return left === right
    })

    return matchingRule?.color ? { background: matchingRule.color } : null
  }

  if (!props.table || props.table.loadError)
    return props.labels ? <WidgetDatasetState table={props.table} labels={props.labels} /> : null
  return (
    <div className="datox-react-dashboard__table-container" data-paged-table={pagination ? "" : undefined}>
      <div className="datox-react-dashboard__table-viewport" ref={viewport}>
        <table
          className={`datox-react-dashboard__table ${densityClass} ${props.style?.stripedRows ? "datox-react-dashboard__table--striped" : ""} ${props.style?.headerFill === false ? "datox-react-dashboard__table--plain-header" : ""}`}
        >
          {props.style?.showHeader === false ? null : (
            <thead>
              <tr>
                {headers.map((header) => (
                  <th key={header}>{props.data?.columnLabels?.[header] || header}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {displayed.map((row, index) => (
              <tr key={index}>
                {headers.map((header) => (
                  <td key={header} style={getCellConditionalStyle(row, header) || undefined}>
                    {formattedCell(row[header] ?? props.missingLabel, props.data?.columnFormats?.[header])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {summary ? (
            <tfoot>
              <tr>
                {headers.map((header, index) => (
                  <td key={header}>
                    {summary[header] === "" && index === 0
                      ? props.labels?.summaryLabel
                      : formattedCell(
                          summary[header],
                          props.data?.summaryColumns?.[header] === "count"
                            ? { format: "integer" }
                            : props.data?.columnFormats?.[header],
                        )}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
        {!rows.length && props.labels ? (
          <div className="datox-react-dashboard__widget-state" role="status">
            {props.labels.noMatchingRows}
          </div>
        ) : null}
      </div>
      {summary ? (
        <details className="datox-react-dashboard__table-scope">
          <summary>{props.labels?.summaryScopeShort || props.labels?.summaryScope}</summary>
          <p>{props.labels?.summaryScope}</p>
        </details>
      ) : null}
      {pagination ? (
        <div className="datox-react-dashboard__table-pagination">
          <span role="status" className="datox-react-dashboard__row-range">
            {props.labels?.rowRange
              ?.replace("{start}", String(rows.length ? (current - 1) * size + 1 : 0))
              .replace("{end}", String(Math.min(current * size, rows.length)))
              .replace("{total}", String(rows.length))}
          </span>
          <div className="datox-react-dashboard__page-navigation">
            <button
              type="button"
              className="datox-react-dashboard__page-button"
              aria-label={props.labels?.previousPage}
              title={props.labels?.previousPage}
              disabled={current <= 1}
              onClick={(event) => {
                event.stopPropagation()
                selectPage(current - 1)
              }}
            >
              <TbChevronLeft size={16} aria-hidden="true" />
            </button>
            <span className="datox-react-dashboard__page-status">
              {props.labels?.pageStatus?.replace("{page}", String(current)).replace("{pages}", String(pages))}
            </span>
            <button
              type="button"
              className="datox-react-dashboard__page-button"
              aria-label={props.labels?.nextPage}
              title={props.labels?.nextPage}
              disabled={current >= pages}
              onClick={(event) => {
                event.stopPropagation()
                selectPage(current + 1)
              }}
            >
              <TbChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
          {pages > 2 ? (
            <label className="datox-react-dashboard__page-jump">
              <span>{props.labels?.goToPage}</span>
              <EditorNumberInput min={1} max={pages} value={current} onChange={selectPage} />
            </label>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function parseComparableNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  if (value == null) return null
  const cleaned = String(value).replace(/[\s,]/g, "")
  if (!cleaned) return null
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

export function applyWidgetTableSettings(table, data) {
  if (!table) return table
  const filters = data?.filters || []
  const columns = data?.columns?.length
    ? data.columns.filter((column) => table.headers.includes(column))
    : table.headers
  const filteredRows = filters.reduce(
    (currentRows, filter) => currentRows.filter((row) => rowMatchesFilter(row, filter)),
    table.rows,
  )
  const sortedRows = sortRows(filteredRows, data?.sort)
  const rows = data?.rowLimit ? sortedRows.slice(0, Number(data.rowLimit)) : sortedRows
  return { ...table, headers: columns, rows }
}

export function applyDashboardFilters(table, widget, filterWidgets) {
  if (!table || widget.type === "filter") return table
  const matchingFilters = filterWidgets.flatMap((filterWidget) => {
    const target = (filterWidget.data?.targets || []).find((item) => item.dataset_id === widget.data?.dataset_id)
    if (!target?.column) return []
    return [{ column: target.column, values: filterWidget.data?.selectedValues || [] }]
  })
  if (!matchingFilters.length) return table
  return {
    ...table,
    rows: table.rows.filter((row) =>
      matchingFilters.every((filter) => filter.values.includes(String(row[filter.column] ?? ""))),
    ),
  }
}

function nextFilterSelectedValues(widget, value, selectedValues) {
  if (!value) return []
  if (widget.style?.multi === false) return selectedValues.includes(value) ? [] : [value]
  return selectedValues.includes(value) ? selectedValues.filter((item) => item !== value) : [...selectedValues, value]
}

function sortRows(rows, sort) {
  if (!sort?.column) return rows
  return [...rows].sort((first, second) => {
    const result = compareDashboardCells(first[sort.column], second[sort.column])
    return sort.direction === "desc" ? -result : result
  })
}

function csvCell(value) {
  const text = String(value ?? "")
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function rowMatchesFilter(row, filter) {
  if (!filter?.column) return true
  const value = String(row[filter.column] ?? "")
  if (filter.operator === "notEmpty") return value.trim().length > 0
  if (filter.operator === "equals") return value.toLowerCase() === String(filter.value ?? "").toLowerCase()
  return value.toLowerCase().includes(String(filter.value ?? "").toLowerCase())
}

function FilterWidget(props) {
  const values = [
    ...new Set((props.table?.rows || []).map((row) => String(row[props.label || ""] ?? "-")).slice(0, 12)),
  ]
  if (props.style?.display === "dropdown")
    return (
      <select
        className="datox-react-dashboard__filter-select"
        value={props.selectedValues[0] || ""}
        onChange={(event) => props.onToggle?.(event.currentTarget.value)}
      >
        <option value="">All</option>
        {values.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>
    )
  return (
    <div className="datox-react-dashboard__filters">
      {values.map((value) => (
        <button
          key={value}
          type="button"
          className={
            props.selectedValues.includes(value)
              ? "datox-react-dashboard__filter-chip datox-react-dashboard__filter-chip--active"
              : "datox-react-dashboard__filter-chip"
          }
          onClick={() => props.onToggle?.(value)}
        >
          {value}
        </button>
      ))}
    </div>
  )
}

function WidgetDatasetState(props) {
  const context = useContext(DashboardTableContext)
  const [retrying, setRetrying] = useState(false)
  const busy = context.loading || retrying
  return (
    <div
      className="datox-react-dashboard__widget-state"
      role={props.table?.loadError && !busy ? "alert" : "status"}
      aria-busy={Boolean(busy)}
    >
      <p>
        {busy
          ? props.labels.tableLoading
          : props.table?.loadError
            ? props.labels.datasetLoadError
            : props.labels.selectDatasetHelp}
      </p>
      {props.table?.loadError && context.onRetry ? (
        <button
          className="datox-react-dashboard__button"
          type="button"
          disabled={Boolean(busy)}
          onClick={() => {
            setRetrying(true)
            Promise.resolve(context.onRetry())
              .catch(() => {})
              .finally(() => setRetrying(false))
          }}
        >
          {props.labels.retryDataset}
        </button>
      ) : null}
    </div>
  )
}

function ChartWidget(props) {
  if (!props.table || props.table.loadError) return <WidgetDatasetState table={props.table} labels={props.labels} />
  const rows = props.table?.rows || []
  if (
    props.widget.type !== "scatter" &&
    props.widget.data?.aggregation &&
    !["sum", "avg", "min", "max", "count"].includes(props.widget.data.aggregation)
  )
    return <div role="alert">{props.labels.invalidChartBindings}</div>
  if (
    (props.widget.data?.filters || []).some(
      (filter) => filter.column && !props.table?.headers?.includes(filter.column),
    ) ||
    (props.widget.data?.sort?.column && !props.table?.headers?.includes(props.widget.data.sort.column))
  )
    return <div role="alert">{props.labels.invalidChartBindings}</div>
  const metric = props.widget.data?.metric
  const measures =
    props.widget.data?.measures?.length && ["bar", "line", "multiSeries"].includes(props.widget.type)
      ? props.widget.data.measures
      : [metric || (props.widget.data?.aggregation === "count" ? "__count__" : "")].filter(Boolean)
  if (
    props.widget.type === "scatter" &&
    (!props.table?.headers?.includes(props.widget.data?.xColumn) ||
      !props.table?.headers?.includes(props.widget.data?.yColumn) ||
      props.widget.data.xColumn === props.widget.data.yColumn ||
      (props.widget.data.sizeColumn && !props.table.headers.includes(props.widget.data.sizeColumn)) ||
      (props.widget.data.sizeColumn &&
        rows.length > 0 &&
        !rows.some((row) => numericCell(row[props.widget.data.sizeColumn]) !== null)) ||
      (rows.length > 0 &&
        !rows.some(
          (row) =>
            numericCell(row[props.widget.data.xColumn]) !== null &&
            numericCell(row[props.widget.data.yColumn]) !== null,
        )))
  )
    return <div role="alert">{props.labels.invalidScatterBindings}</div>
  if (
    props.widget.type !== "scatter" &&
    (!props.table?.headers?.includes(props.widget.data?.label) ||
      !measures.length ||
      measures.some(
        (measure) =>
          measure !== "__count__" &&
          (!props.table?.headers?.includes(measure) ||
            (props.widget.data?.aggregation !== "count" &&
              rows.length > 0 &&
              !rows.some((row) => numericCell(row[measure]) !== null))),
      ) ||
      (props.widget.data?.topBy &&
        (!props.table?.headers?.includes(props.widget.data.topBy) ||
          (rows.length > 0 && !rows.some((row) => numericCell(row[props.widget.data.topBy]) !== null)))))
  )
    return <div role="alert">{props.labels.invalidChartBindings}</div>
  const items = chartItems(rows, props.widget.data, props.widget.type, props.labels)
  const option = chartOption(
    props.widget.type,
    items,
    props.accent,
    props.labels,
    measures,
    { __count__: props.labels.aggregationCount, ...(props.widget.data?.displayNames || {}) },
    { ...(props.widget.style || {}), width: props.widget.layout?.w, height: props.widget.layout?.h },
  )
  return <ResponsiveChart option={option} />
}

function ResponsiveChart(props) {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof ResizeObserver === "undefined") return undefined
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect || container.getBoundingClientRect()
      const width = Math.round(rect.width || 0)
      const height = Math.round(rect.height || 0)
      if (width > 0 && height > 0)
        setSize((current) => (current.width === width && current.height === height ? current : { width, height }))
      chartRef.current?.getEchartsInstance?.().resize()
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const resizeFrame = requestAnimationFrame(() => chartRef.current?.getEchartsInstance?.().resize())
    return () => cancelAnimationFrame(resizeFrame)
  }, [props.option, size.width, size.height])

  return (
    <div ref={containerRef} className="datox-react-dashboard__chart-wrap">
      <ReactECharts
        key={`${size.width}x${size.height}`}
        ref={chartRef}
        option={props.option}
        notMerge
        lazyUpdate
        style={{ width: size.width || "100%", height: size.height || "100%" }}
        opts={{ renderer: "canvas" }}
      />
    </div>
  )
}

function chartOption(type, items, accent, labels, measures = [], displayNames = {}, style = {}) {
  const width = Number(style.width || 0)
  const height = Number(style.height || 0)
  const compact = width < 340 || height < 220
  const tiny = width < 260 || height < 160
  const formatValue = (value) => {
    return formatStyledNumber(value, style)
  }
  const escapeText = (value) =>
    String(value).replace(
      /[&<>"']/g,
      (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char],
    )
  const palette = [accent, "#7048e8", "#40a9ff", "#339989", "#d6334c", "#e8590c", "#1d4ed8"]
  const seriesForMeasures = (seriesType) =>
    measures.map((measure, index) => ({
      name: displayNames[measure] || measure,
      type: seriesType,
      smooth: seriesType === "line",
      stack: style.stackSeries ? "total" : undefined,
      label: style.showValueLabels
        ? {
            show: true,
            position: style.orientation === "horizontal" ? "right" : "top",
            formatter: (params) => formatValue(params.value),
          }
        : { show: false },
      itemStyle: {
        color: style.useDistinctBarColors ? undefined : index === 0 ? accent : palette[index % palette.length],
      },
      lineStyle: { color: index === 0 ? accent : palette[index % palette.length], width: 3 },
      data: items.map((item, itemIndex) =>
        style.useDistinctBarColors && seriesType === "bar"
          ? { value: item.values[measure] || 0, itemStyle: { color: palette[itemIndex % palette.length] } }
          : item.values[measure] || 0,
      ),
    }))
  if (type === "pie")
    return {
      tooltip: { valueFormatter: formatValue },
      legend: style.showLegend !== false && !tiny ? {} : { show: false },
      series: [
        {
          type: "pie",
          radius: style.donut === false ? [0, "68%"] : ["36%", "68%"],
          label: {
            show: style.showValueLabels !== false && !tiny,
            formatter: (params) =>
              style.showValuesNextToLabels ? `${params.name} ${formatValue(params.value)}` : params.name,
            color: "#344054",
            fontSize: compact ? 10 : 12,
          },
          labelLine: { show: style.showValueLabels !== false && !tiny },
          data: items,
        },
      ],
    }
  if (type === "treemap")
    return { tooltip: {}, series: [{ type: "treemap", roam: false, label: { show: !tiny }, data: items }] }
  if (type === "funnel")
    return {
      tooltip: {},
      legend: style.showLegend !== false && !tiny ? {} : { show: false },
      series: [{ type: "funnel", label: { show: style.showValueLabels && !tiny }, data: items }],
    }
  if (type === "scatter")
    return {
      tooltip: {
        formatter: (params) =>
          `${params.name ? `${escapeText(params.name)}<br/>` : ""}${escapeText(style.xAxisLabel || labels.scatterX)}: ${formatValue(params.value[0])}<br/>${escapeText(style.yAxisLabel || labels.scatterY)}: ${formatValue(params.value[1])}${params.value[2] == null ? "" : `<br/>${escapeText(labels.scatterSize)}: ${formatValue(params.value[2])}`}`,
      },
      grid: { left: compact ? 24 : 36, right: 12, top: 18, bottom: compact ? 20 : 32, containLabel: true },
      xAxis: {
        type: "value",
        name: tiny ? undefined : style.xAxisLabel || undefined,
        axisLabel: { show: style.showValueAxisLabels !== false && !tiny, formatter: formatValue },
        splitLine: { show: style.showGrid !== false && !tiny },
      },
      yAxis: {
        type: "value",
        name: tiny ? undefined : style.yAxisLabel || undefined,
        axisLabel: { show: style.showValueAxisLabels !== false && !tiny, formatter: formatValue },
        splitLine: { show: style.showGrid !== false && !tiny },
      },
      series: [
        {
          type: "scatter",
          symbolSize: (value) =>
            value[2] == null
              ? tiny
                ? 7
                : compact
                  ? 9
                  : 12
              : Math.max(6, Math.min(36, Math.sqrt(Math.max(0, value[2])) * 4)),
          itemStyle: { color: accent },
          data: items.map((item) => ({ name: item.name, value: [item.x, item.value, item.size] })),
        },
      ],
    }
  if (type === "heatmap")
    return {
      tooltip: {},
      grid: { left: compact ? 20 : 36, right: 12, top: 18, bottom: compact ? 20 : 32, containLabel: true },
      xAxis: { type: "category", data: items.map((item) => item.name), axisLabel: { show: !tiny } },
      yAxis: { type: "category", data: [labels.valueLabel], axisLabel: { show: !tiny } },
      visualMap: {
        show: false,
        min: 0,
        max: Math.max(1, ...items.map((item) => item.value)),
        inRange: { color: ["#e7f0ff", accent] },
      },
      series: [
        {
          type: "heatmap",
          label: { show: style.showValueLabels && !tiny },
          data: items.map((item, index) => [index, 0, item.value]),
        },
      ],
    }
  if (type === "line" || type === "multiSeries")
    return {
      tooltip: { valueFormatter: formatValue },
      legend:
        style.showLegend !== false && measures.length > 1 && !tiny
          ? { top: 0, textStyle: { fontSize: compact ? 10 : 12 } }
          : { show: false },
      grid: {
        left: compact ? 24 : 36,
        right: 12,
        top: style.showLegend !== false && measures.length > 1 && !tiny ? 36 : 14,
        bottom: compact ? 20 : 32,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        name: tiny ? undefined : style.xAxisLabel || undefined,
        axisLabel: {
          show: style.showValueAxisLabels !== false && !tiny,
          fontSize: compact ? 9 : 11,
          hideOverlap: true,
        },
        data: items.map((item) => item.name),
      },
      yAxis: {
        type: "value",
        name: tiny ? undefined : style.yAxisLabel || undefined,
        axisLabel: {
          show: style.showValueAxisLabels !== false && !tiny,
          formatter: formatValue,
          fontSize: compact ? 9 : 11,
        },
        splitLine: { show: style.showGrid !== false && !tiny },
      },
      series: seriesForMeasures("line").map((series) => ({
        ...series,
        symbolSize: compact ? 3 : 5,
        showSymbol: measures.length <= 4 && !tiny,
      })),
    }
  const horizontal = style.orientation === "horizontal"
  const barSeries = seriesForMeasures("bar").map((series) => ({
    ...series,
    itemStyle: { ...series.itemStyle, borderRadius: horizontal ? [0, 8, 8, 0] : [8, 8, 0, 0] },
  }))
  return {
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: formatValue },
    legend:
      style.showLegend !== false && measures.length > 1 && !tiny
        ? { top: 0, textStyle: { fontSize: compact ? 10 : 12 } }
        : { show: false },
    grid: {
      left: compact ? 24 : 36,
      right: style.showValueLabels && !tiny ? 44 : 12,
      top: style.showLegend !== false && measures.length > 1 && !tiny ? 36 : style.showValueLabels && !tiny ? 24 : 14,
      bottom: compact ? 20 : 32,
      containLabel: true,
    },
    xAxis: horizontal
      ? {
          type: "value",
          name: tiny ? undefined : style.yAxisLabel || undefined,
          axisLabel: {
            show: style.showValueAxisLabels !== false && !tiny,
            formatter: formatValue,
            fontSize: compact ? 9 : 11,
          },
          splitLine: { show: style.showGrid !== false && !tiny },
        }
      : {
          type: "category",
          name: tiny ? undefined : style.xAxisLabel || undefined,
          data: items.map((item) => item.name),
          axisLabel: {
            show: style.showValueAxisLabels !== false && !tiny,
            rotate: compact || items.length > 4 ? 24 : 0,
            overflow: "truncate",
            fontSize: compact ? 9 : 11,
          },
        },
    yAxis: horizontal
      ? {
          type: "category",
          name: tiny ? undefined : style.xAxisLabel || undefined,
          data: items.map((item) => item.name),
          inverse: true,
          axisLabel: {
            show: style.showValueAxisLabels !== false && !tiny,
            overflow: "truncate",
            fontSize: compact ? 9 : 11,
          },
        }
      : {
          type: "value",
          name: tiny ? undefined : style.yAxisLabel || undefined,
          axisLabel: {
            show: style.showValueAxisLabels !== false && !tiny,
            formatter: formatValue,
            fontSize: compact ? 9 : 11,
          },
          splitLine: { show: style.showGrid !== false && !tiny },
        },
    series: barSeries.map((series) => ({ ...series, barMaxWidth: compact ? 24 : 48 })),
  }
}

function chartTypeIcon(type) {
  if (type === "bar") return "▥"
  if (type === "line") return "⌁"
  if (type === "scatter") return "○"
  if (type === "pie") return "◔"
  if (type === "treemap") return "▦"
  if (type === "funnel") return "▽"
  return "▥"
}

function isChartWidget(type) {
  return ["bar", "line", "scatter", "pie", "heatmap", "multiSeries", "treemap", "funnel"].includes(type)
}

function chartDataSummary(widget, labels) {
  if (widget.type === "scatter")
    return labels.scatterBindingSummary
      .replace("{x}", widget.data?.xColumn || labels.noBinding)
      .replace("{y}", widget.data?.yColumn || labels.noBinding)
  const measures = widget.data?.measures?.length
    ? widget.data.measures.join(", ")
    : widget.data?.metric || labels.metricFallback
  return `${measures} ${labels.by} ${widget.data?.label || labels.labelColumn}`
}

function createWidget(type, index, table, labels, position) {
  const widgetLabels = Object.fromEntries(WIDGETS.map((item) => [item.type, widgetLabel(labels, item.type)]))
  const metric = firstNumericColumn(table) || table?.headers?.[0]
  const label = table?.headers?.[0]
  const x = position?.x ?? 16 + (index % 2) * 540
  const y = position?.y ?? 16 + Math.floor(index / 2) * 260
  if (type === "header")
    return { id: widgetIdValue(), type, title: widgetLabels[type], layout: { x: 0, y, w: 1123, h: 96 } }
  if (type === "title") return { id: widgetIdValue(), type, title: widgetLabels[type], layout: { x, y, w: 520, h: 68 } }
  if (type === "text")
    return {
      id: widgetIdValue(),
      type,
      title: widgetLabels[type],
      text: labels.textPlaceholder,
      layout: { x, y, w: 520, h: 152 },
    }
  if (type === "kpi")
    return {
      id: widgetIdValue(),
      type,
      title: widgetLabels[type],
      layout: { x, y, w: 348, h: 152 },
      data: { dataset_id: table?.datasetID, metric },
    }
  if (type === "table" || type === "freeTable")
    return {
      id: widgetIdValue(),
      type,
      title: widgetLabels[type],
      layout: { x: 16, y, w: 1091, h: 320 },
      data:
        type === "freeTable"
          ? { freeStyle: freeStyleFromTable(undefined, labels) }
          : { dataset_id: table?.datasetID, columns: [] },
      style: { cellColorRules: [] },
    }
  if (type === "filter")
    return {
      id: widgetIdValue(),
      type,
      title: widgetLabels[type],
      layout: { x, y, w: 320, h: 120 },
      data: {
        dataset_id: table?.datasetID,
        label,
        selectedValues: [],
        targets: table?.datasetID ? [{ dataset_id: table.datasetID, column: label }] : [],
      },
    }
  return {
    id: widgetIdValue(),
    type,
    title: widgetLabels[type],
    layout: { x, y, w: 520, h: 320 },
    data: {
      dataset_id: table?.datasetID,
      metric,
      label,
      ...(type === "scatter"
        ? {
            xColumn: table?.headers?.find((header) => header !== metric && numericValues(table, header).length) || "",
            yColumn: metric,
          }
        : {}),
    },
  }
}

function widgetLabel(labels, type) {
  return labels.widgets[type]
}

function defaultConfig(title, tabName) {
  return {
    title,
    canvas: { orientation: "landscape", width: 1123, height: 794 },
    tabs: [{ id: "tab-1", name: tabName, widgets: [] }],
    design: { theme: "light", accent: "#2f80ed" },
  }
}

function designFont(value) {
  if (value === "Serif") return "Georgia, serif"
  if (value === "Inter") return "Inter, ui-sans-serif, system-ui, sans-serif"
  return "Inter, ui-sans-serif, system-ui, sans-serif"
}

function verticalAlignValue(value) {
  if (value === "bottom") return "flex-end"
  if (value === "center") return "center"
  return "flex-start"
}

function formatStyledNumber(value, style = {}) {
  const number = numericCell(value)
  if (number == null) return "--"
  if (style.decimalPlaces == null) {
    const formatted = formatNumber(style.valueFormat === "integer" ? Math.round(number) : number)
    return style.valueFormat === "percent" ? `${formatted}%` : formatted
  }
  return formattedCell(number, {
    format: style.valueFormat === "integer" ? "integer" : style.valueFormat === "percent" ? "percent" : "number",
    decimals: style.decimalPlaces,
  })
}

function cloneDashboardConfig(value) {
  return JSON.parse(JSON.stringify(value))
}

async function waitForDashboardPaint() {
  await document.fonts?.ready
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
  await new Promise((resolve) => window.setTimeout(resolve, 120))
}

function datasetColumns(datasetID, outputTables, datasets) {
  const table = outputTables.find((item) => item.datasetID === datasetID)
  if (table) return table.headers
  return datasets.find((item) => item.id === datasetID)?.columns?.map((column) => column.name) || []
}

function uniqueColumnValues(table, column) {
  if (!table || !column) return []
  return [...new Set(table.rows.map((row) => String(row[column] ?? "")).filter(Boolean))].sort((first, second) =>
    first.localeCompare(second),
  )
}

function firstNumericColumn(table) {
  return table?.headers?.find((header) => numericValues(table, header).length > 0)
}

export function numericValues(table, metric) {
  if (!table || !metric) return []
  return table.rows
    .map((row) => Number(String(row[metric] ?? "").replace(/,/g, "")))
    .filter((value) => Number.isFinite(value))
}

function formatNumber(value) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)
}

function sourceRowColumns(rows) {
  return [...new Set((rows || []).flatMap((row) => Object.keys(row || {})))]
}

function formatSourceValue(value) {
  if (value == null) return ""
  return typeof value === "object" ? JSON.stringify(value) : String(value)
}

function DashboardMarkdown({ text, className }) {
  const [html, setHtml] = useState("")
  useEffect(() => {
    let active = true
    setHtml("")
    dashboardMarkdownHtml(text)
      .then((value) => {
        if (active) setHtml(value)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [text])
  if (!html) return <div className={className}>{text}</div>
  return <div className={className} data-component="markdown" dangerouslySetInnerHTML={{ __html: html }} />
}

function widgetIdValue() {
  return `widget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function uniqueReviewers(reviewers) {
  const seen = new Set()
  return reviewers.flatMap((reviewer) => {
    const email = reviewer.email.trim().toLowerCase()
    if (!email || seen.has(email)) return []
    seen.add(email)
    return [{ name: reviewer.name?.trim() || "", email, clientValue: reviewer.clientValue || "" }]
  })
}

function hasDuplicateReviewerEmails(reviewers) {
  const emails = reviewers.map((reviewer) => reviewer.email.trim().toLowerCase()).filter(Boolean)
  return new Set(emails).size !== emails.length
}
